// CanvasDrawingEngine — Phase 3 後半 real implementation.
//
// Public API contract (matches the Phase 3 前半 stub, frontend-developer
// uses these symbols verbatim from `DrawingCanvas.tsx`):
//
//   class CanvasDrawingEngine {
//     constructor(canvas: HTMLCanvasElement)
//     getCanvas(): HTMLCanvasElement
//     startStroke(input: StartStrokeInput): { sid: string }
//     addPoints(sid: string, points: ReadonlyArray<StrokePoint>): void
//     endStroke(sid: string): void
//     applyRemoteStroke(events: ReadonlyArray<unknown>): void
//     resize(width: number, height: number): void
//     dispose(): void
//   }
//
// Architecture (architecture.md §16 / §19):
//
//   PointerEvent
//      ↓ (in DrawingCanvas.tsx)
//   engine.startStroke / addPoints / endStroke
//      ↓ (in this file)
//   PointFilter (identity in MVP, smoothing in β)  — §19.10 C
//      ↓
//   ┌─ branch ───────────────────────────────────────────────────────────┐
//   │ if `OffscreenCanvas` + `Worker` are available:                      │
//   │   → postMessage to drawingWorker.ts (off-main-thread render)        │
//   │ else (older browsers / node tests / SSR):                           │
//   │   → run EngineCore inline on the main thread                        │
//   └────────────────────────────────────────────────────────────────────┘
//      ↓
//   StrokeRenderer ⇄ Layer[] ⇄ stroke buffers
//      ↓                          (architecture.md §19.1 — 2層レンダリング)
//   composite + alpha-blend → display canvas
//
// The fallback path matters more than it sounds: it lets us run the
// engine in vitest's `node` env (no DOM canvas needed for the rasterizer
// core) AND it lets us ship to browsers that haven't enabled
// OffscreenCanvas yet without a code path divergence.

import {
  applyRegionMove,
  clampLayerOpacity,
  type DisplayBackend,
  decodeRgbaPng,
  EngineCore,
  type ImagepasteBitmapEvictedInfo,
  type Rect,
  type RegionImage,
  type SpuitMode,
  UndoBaselineStore,
} from '@oekaki/drawing-core';
import type {
  PenParams,
  StrokeCancelRejected,
  StrokeMask,
  StrokePen,
  StrokePoint,
  WhiteboardRegionCancelRejected,
} from '@oekaki/protocol';
import {
  snapshotPublishedSchema,
  strokeCancelledBroadcastSchema,
  strokeEndBroadcastSchema,
  strokeSegmentBroadcastSchema,
  strokeStartBroadcastSchema,
  whiteboardClearBroadcastSchema,
  whiteboardImagepasteSchema,
  whiteboardRegionCancelledSchema,
} from '@oekaki/protocol';
import { type CancellableOp, isSameOp } from '../../application/undoOps.js';
import { decodeBitmapToRgba } from '../canvas2d/rasterCanvas.js';
import {
  encodeRgbaToPngBlob,
  type ImagepastePngBlobFn,
  type ImagepasteResampleFn,
  normalizeImagepasteSize,
  resampleBitmapToRgba,
} from './imagepasteResample.js';
import {
  isInputLatencyEnabled,
  setInputLatencyEnabled,
  takeInputLatencyMarks,
} from './inputLatencyProbe.js';
import { createOpacityPreviewSurface } from './opacityPreviewSurface.js';
import {
  createIdentityPointFilter,
  createPointFilterForLevel,
  type PointFilter,
  type StabilizerLevel,
} from './PointFilter.js';
import { RemoteCoalesceQueue } from './RemoteCoalesceQueue.js';
import { parseRegionBroadcast, type RegionBroadcast } from './regionBroadcast.js';
import { ThumbnailJankSampler } from './thumbnailJankSampler.js';
import type {
  EngineWorkerInbound,
  EngineWorkerOutbound,
  LeadPenMetaMsg,
} from './worker/workerProtocol.js';
import {
  clampZoom,
  DEFAULT_DOT_BY_DOT_MODE,
  type DotByDotMode,
  displayScaleFor,
  dotByDotDivisor,
  ZOOM_MIN,
} from './zoom.js';

// ─── Public API surface ────────────────────────────────────────────────────

/** A finished layer thumbnail (straight-α RGBA, `width * height * 4`). */
/**
 * 「元に戻す」の拒否通知 (unicast)。線 (`stroke.cancel-rejected`) と範囲の移動・コピー
 * (`whiteboard.region-cancel-rejected`) で reason / conflicts / retryAfterMs は共通。
 */
export type UndoCancelRejection = StrokeCancelRejected | WhiteboardRegionCancelRejected;

export type LayerThumbnailState = 'off' | 'on' | 'stopped';

export interface LayerThumbnailMessage {
  readonly layerId: number;
  readonly width: number;
  readonly height: number;
  readonly rgba: Uint8ClampedArray;
}

/** Inputs accepted by `startStroke` (matches the Phase 3 前半 stub shape). */
export interface StartStrokeInput {
  pen: StrokePen;
  /** RGB24 (0..0xFFFFFF). Eraser ignores at render. */
  color: number;
  /** 0..255. */
  alpha: number;
  /** Width in CSS px. */
  width: number;
  /** MVP は常に 0、β レイヤー機能で 0..N。 */
  layerId: number;
  mask: StrokeMask;
  /**
   * Anti-aliased stamping (modern-layout.md §9.9). Default `false` → pre-AA
   * byte-identical path (Classic 無退行). The local send path leaves this
   * unset today (frontend wires the Modern toggle in a separate task); remote
   * receive forwards the broadcast `aa`.
   */
  aa?: boolean;
  /**
   * Pen-specific parameters (texture-brush-design.md §4 案B / §11 step 3). For
   * `pen='texture'` carries `{ kind:'texture', preset }` so the renderer
   * resolves the matching sampler; omitted → 紙 default. Threaded straight
   * through to the worker / inline `EngineCore.startStroke`.
   */
  penParams?: PenParams;
}

/** Engine constructor options. Tests can opt into the inline-only path. */
export interface CanvasDrawingEngineOptions {
  /** Override the default identity PointFilter (architecture.md §19.10 C). */
  readonly pointFilter?: PointFilter;
  /** ドットバイドット表示の初期設定 (既定 'auto')。後から `setDotByDotMode` でも変更可。 */
  readonly dotByDotMode?: DotByDotMode;
  /**
   * Force the inline (main-thread) renderer even when `OffscreenCanvas` +
   * `Worker` are available. Default `false`. Used by tests.
   */
  readonly forceInline?: boolean;
  /**
   * Factory for the Web Worker. Defaults to a Vite-friendly factory that
   * the embedder must inject (Vite resolves `?worker` imports at build
   * time, so the engine itself can't `new Worker(new URL(…))` portably).
   *
   * MVP behaviour: if no factory is provided, we fall back to the inline
   * renderer regardless of OffscreenCanvas availability. β can wire the
   * Vite-bundled factory in `main.tsx` once it's measured the perf win.
   */
  readonly workerFactory?: () => Worker;
  /**
   * Idle-stroke watchdog cutoff in ms. The §19.7 belt-and-braces defence
   * against stuck `alpha` strokes when a peer disconnects mid-stroke
   * without a clean `stroke.end`. Default 30 000 ms.
   */
  readonly idleStrokeTimeoutMs?: number;
  /**
   * 2026-05-23 — absolute cap on the number of **remote** stroke points
   * buffered in the coalesce queue before decimation kicks in
   * (docs/_wip/backpressure-coalesce.md). This is a memory-safety net only;
   * realistic load (even backgrounded) never reaches it. On overflow the
   * queue thins (decimates) the largest buffered run rather than dropping it,
   * so curves degrade in resolution rather than collapsing to chords.
   *
   * Default `REMOTE_COALESCE_POINT_CAP` (50 000). Override (small) in tests
   * to exercise the decimation path.
   */
  readonly remotePointCap?: number;
  /**
   * Initial canvas background color. Default opaque white (0xFFFFFF).
   * Mirrors the legacy startup state where the canvas was painted opaque
   * white at room init.
   */
  readonly background?: { rgb: number; alpha: number };
  /**
   * Phase 7 Task 7-8a — `whiteboard.imagepaste` broadcast handler hooks.
   * `applyRemoteStroke` / `applyImagepaste` use these to fetch the
   * presigned GET URL, decode the PNG into a bitmap, and read the bitmap
   * back as RGBA bytes. The defaults forward to `globalThis.fetch`,
   * `globalThis.createImageBitmap`, and an `OffscreenCanvas`-based
   * decoder — same shape as `SnapshotLoader`'s deps so tests can stub.
   */
  readonly imagepasteFetchFn?: ImagepasteFetchFn;
  readonly imagepasteCreateBitmapFn?: ImagepasteCreateBitmapFn;
  readonly imagepasteDecodeBitmapFn?: ImagepasteDecodeBitmapFn;
  /** imagepaste リサイズ (A案): 元 bitmap → 目標サイズの RGBA。既定は `resampleBitmapToRgba`。 */
  readonly imagepasteResampleFn?: ImagepasteResampleFn;
  /** imagepaste リサイズ (A案): RGBA → PNG Blob。既定は `encodeRgbaToPngBlob`。 */
  readonly imagepastePngBlobFn?: ImagepastePngBlobFn;
  /**
   * 範囲の移動・コピー (`whiteboard.region`) の画像の decode。canvas を通さない純 JS
   * (straight-alpha のまま、半透明が変わらない)。既定は drawing-core の `decodeRgbaPng`。
   * 画像の取得自体は `imagepasteFetchFn` を共用する (`res.arrayBuffer()` を読む)。
   */
  readonly regionPngDecodeFn?: (bytes: Uint8Array) => RegionImage;
  /**
   * W3 Classic レイヤー追加 (2026-05-26) — encode a full-canvas RGBA composite
   * into a PNG `Blob` for `exportAllLayersBlob` (Classic camera capture).
   * The default renders the RGBA into a detached `<canvas>` and reads back
   * `toBlob('image/png')`. Tests inject a stub (the node env has no DOM
   * canvas). Returns `null` if encoding is unavailable.
   */
  readonly pngEncodeFn?: PngEncodeFn;
}

/** Public preview state observed by `onImagepastePreviewChange`. */
export interface ImagepastePreviewState {
  /** Top-left in canvas (= layer) coordinates. */
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** True after `lockImagepastePreview` (drag disabled while uploading). */
  readonly locked: boolean;
}

// ─── Phase 6.9 — name label / zoom public types ──────────────────────────────

/**
 * A single name-label entry surfaced by the engine to UI subscribers.
 *
 * `(x, y)` are in **canvas (layer) coordinates** — same coordinate system as
 * pen strokes. The UI layer multiplies by the current zoom factor when
 * positioning the DOM overlay, so the labels track the canvas at any zoom.
 *
 * **Per-uid 1-label, real-time tracking** (Phase 6.9 仕様変更 2026-05-06):
 * the engine surfaces **at most one label per `uid`**, and updates `(x, y)`
 * on **every** incoming `stroke.start` / `stroke.segment` for that uid so
 * the label appears to "follow" the peer's brush in real time (the
 * 「一緒に描いている」感 UX). `stroke.end` does not update `(x, y)` — the
 * last segment's tail point already reflects the final position.
 *
 * `seq` is included for traceability (events.bin replay / debug), not
 * de-duplication; it is taken from the originating `stroke.start` and
 * stays constant across that stroke's segment updates.
 *
 * `name` is the broadcast-stamped `displayName` from the originating
 * `stroke.start` — frozen at draw time so the label survives subsequent
 * member-left / rename events (architecture.md §6.9, protocol-events.md §1).
 *
 * `birthtime` is the epoch-ms timestamp of the most recent update for this
 * label slot (= the `Date.now()` when the latest `stroke.segment` mirrored
 * its tail point into this slot). Used for the **1-second TTL auto-expire**
 * (Phase 6.9 仕様追加 2026-05-06, legacy 踏襲): the engine's expire timer
 * removes labels whose `birthtime` is older than `NAME_LABEL_TTL_MS`. As
 * long as `stroke.segment`s keep arriving, `birthtime` is refreshed on
 * every advance and the label persists; once the peer stops drawing for
 * more than 1 second, the label disappears. The frontend does NOT consume
 * `birthtime` directly — the engine handles expiry internally and notifies
 * subscribers via `onNameLabelsChange`.
 */
export interface NameLabel {
  readonly x: number;
  readonly y: number;
  readonly name: string;
  readonly uid: string;
  readonly seq: number;
  readonly birthtime: number;
}

/**
 * NameLabel time-to-live in epoch ms. Labels whose `birthtime` is older
 * than this are removed by the engine's expire pass. Mirrors the legacy
 * `WhiteboardCanvas.drawNameLabel` 1-second cutoff (旧 OekakiChatJava
 * `if (now - nl.birthtime > 1000) namelabels.remove(...)` を踏襲).
 *
 * Kept tunable so Phase 8 観測 can adjust if the UX feedback prefers a
 * shorter / longer cutoff. Exported alongside `ZOOM_MIN` / `ZOOM_MAX` so
 * tests and future callers can reason about the timing precisely without
 * hard-coding magic numbers.
 */
export const NAME_LABEL_TTL_MS = 1000;

/**
 * Number of drawable layers the engine allocates (W3 Classic レイヤー追加,
 * 2026-05-26, docs/_wip/w3-classic-layers.md §2.1).
 *
 * Classic ships **3 fixed** layers (内部 id 0/1/2 = 下から 背 / 中 / 前). No
 * reorder / opacity / blend mode (= Modern / W5 の領分). The composite order
 * is bottom-to-top by id: id0 背 → id1 中 → id2 前.
 *
 * The default active layer (= what `PenConfig.layerId` starts at) is owned by
 * the frontend, not here — the engine just draws strokes onto whichever
 * `layerId` they carry.
 */
export const CLASSIC_LAYER_COUNT = 3;

/**
 * Zoom range constants — re-exported from `./zoom.js` so existing import
 * sites (`import { ZOOM_MIN, ZOOM_MAX } from '…/CanvasDrawingEngine'`) stay
 * valid. M1-a 分数ズーム (modern-phone-shell-2026-07-02.md §3.1): the engine
 * now accepts **continuous (fractional) zoom** down to `ZOOM_FIT_MIN` (0.1x)
 * — the legacy integer-only `Math.trunc` clamp was removed. `ZOOM_MIN` keeps
 * its meaning (= 1x baseline / reset target / integer-step lower bound);
 * the continuous lower bound lives under the separate name `ZOOM_FIT_MIN`.
 * Pure policy (clamp / integer-step snap / fit computation) lives in
 * `zoom.ts` so the UI layer can share it without importing this engine.
 */
export { ZOOM_FIT_MIN, ZOOM_MAX, ZOOM_MIN } from './zoom.js';

export type ImagepasteFetchFn = (url: string, init?: { signal?: AbortSignal }) => Promise<Response>;

export type ImagepasteCreateBitmapFn = (blob: Blob) => Promise<ImageBitmap>;

export type ImagepasteDecodeBitmapFn = (bitmap: ImageBitmap) => {
  rgba: Uint8ClampedArray;
  width: number;
  height: number;
};

/**
 * W3 Classic レイヤー追加 (2026-05-26) — encode a full-canvas RGBA buffer
 * (length = `width * height * 4`) into a PNG `Blob`, or `null` if encoding is
 * unavailable. Used by `exportAllLayersBlob`.
 *
 * 2026-08-13 (capture-download-blob-2026-08-13.md §4.1): dataURL → `Blob`.
 * `toDataURL` の戻りを `<a href>` に載せていたため、**Chromium の 2MB URL 長
 * 制限**で「描き込むと DL が黙って失敗」、**iOS Safari の DL マネージャが
 * `data:` を処理できない**ため iPad で常に失敗していた。`toBlob` は非同期
 * なので戻り値も Promise になる。
 */
export type PngEncodeFn = (
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
) => Promise<Blob | null>;

/**
 * Re-export of the stroke-stabilisation level (手ぶれ補正, §9.8) so UI code
 * can `import { StabilizerLevel } from '…/CanvasDrawingEngine'` alongside the
 * engine it drives. Canonical definition lives in `PointFilter.ts`.
 */
export type { StabilizerLevel } from './PointFilter.js';

/**
 * The engine.
 *
 * Construction: `new CanvasDrawingEngine(canvas)`. The first `resize` call
 * (from React's effect) sets the actual buffer size; before then the
 * engine sizes itself off the canvas's intrinsic dimensions.
 */
export class CanvasDrawingEngine {
  private readonly canvas: HTMLCanvasElement;
  private readonly active = new Set<string>();
  // Active PointFilter (§9.8 stroke stabilisation / §19.10 C input hook).
  // Swapped lazily at `startStroke` when the requested stabiliser level
  // changes, so the strength never shifts mid-stroke.
  private pointFilter: PointFilter;
  // `null` = explicit override was injected via constructor opts (tests /
  // custom filters) → `setStabilizer` is ignored. Otherwise the engine owns
  // the filter and `setStabilizer` recreates it by level. Default `'off'`
  // (= identity, bit-identical to pre-stabiliser behaviour).
  private stabilizerLevel: StabilizerLevel | null;
  private pendingStabilizerLevel: StabilizerLevel | null;
  private readonly idleStrokeTimeoutMs: number;
  /** `setLocalRegionMask` の自動解除対象 uid (null = 手動解除のみ)。 */
  private localMaskReleaseUid: string | null = null;
  private disposed = false;

  // Mode A: inline. The core runs on this thread, draws to a 2D context
  // pulled from `canvas`. Used in tests + when worker is unavailable.
  private inlineCore: EngineCore | null = null;
  // Mode B: worker. Kept opaque so the inline path doesn't need to know.
  private worker: Worker | null = null;
  private workerReady = false;
  /**
   * Queued main→worker messages buffered before `ready` (worker side init
   * is async — see `bootWorker`). Each entry carries an optional
   * `transfer` list so messages that ship `Transferable`s (= the
   * imagepaste preview rgba buffer, drag-perf hot-fix 2026-05-31) keep
   * their zero-copy semantics when the queue flushes.
   */
  private pendingMessages: Array<{
    msg: EngineWorkerInbound;
    transfer?: Transferable[];
  }> = [];

  // ─── PR 2-β (2026-05-17) — worker pixel-request / backpressure ──────────
  //
  // `pixelRequests` keys each in-flight `pixel.request` postMessage by
  // monotonic `requestId` so the matching `pixel.result` resolves the
  // right Promise. Worker death drains every pending request to `null`
  // (= same shape as an OOB sample — UI treats it as "no-op spuit",
  // safe degrade).
  private nextPixelRequestId = 1;
  private readonly pixelRequests = new Map<
    number,
    (pixel: { r: number; g: number; b: number; a: number } | null) => void
  >();

  // 範囲の移動・コピーの写し取り (`region.read.request`/`result`)。pixelRequests と同形。
  private nextRegionReadId = 1;
  private readonly regionReadRequests = new Map<number, (r: RegionReadResult | null) => void>();
  private readonly regionPngDecodeFn: (bytes: Uint8Array) => RegionImage;

  // 自分の移動の「確定から echo 適用まで自分の新しい線を止める」保持
  // (docs/_wip/region-move-2026-10-15.md「描きかけの線との衝突」)。UI が `startStroke` の前に
  // `isOwnStrokeInputHeld()` を見て、保持中はペンを下ろさせない。
  private ownStrokeHold: { uid: string | null; timer: ReturnType<typeof setTimeout> } | null = null;
  private readonly ownStrokeHoldListeners = new Set<(held: boolean) => void>();
  private ownStrokeIdleWaiters: Array<() => void> = [];

  // ─── W3 Classic レイヤー追加 (2026-05-26) — compositeAll round-trip ──────
  //
  // `compositeAllRequests` mirrors `pixelRequests`: each in-flight
  // `compositeAll.request` postMessage keys a resolver that the matching
  // `compositeAll.result` unblocks with the full-canvas RGBA composite
  // (+ dims). Worker death / dispose drains every pending entry to `null`
  // so `exportAllLayersBlob` callers don't hang (the capture handler
  // falls back to the display canvas on `null`).
  private nextCompositeAllRequestId = 1;
  private readonly compositeAllRequests = new Map<
    number,
    (result: { rgba: Uint8ClampedArray; width: number; height: number } | null) => void
  >();

  // ─── PR 2-β follow-up (2026-05-21) — perf-measure bridge over worker ───
  //
  // The drawing-core `perfMeasure` module lives in the worker's JS realm
  // when `useWorker === true`, so the main-thread `enableDrawingPerfMeasure`
  // import doesn't flip the worker's gate. The e2e harness routes the gate
  // toggle + measure buffer drain through these helpers, which postMessage
  // a `perf.enable` / `perf.disable` / `perf.clear` / `perf.collect` and
  // await the matching `perf.ack` / `perf.measures` response.
  //
  // `perfRequests` mirrors `pixelRequests`: each in-flight request keys a
  // resolver that the worker's response unblocks. Crash recovery drains
  // every pending entry to a neutral value (= empty measure array / void)
  // so awaiters don't hang.
  private nextPerfRequestId = 1;
  private readonly perfAckRequests = new Map<number, () => void>();
  // 描き味ベンチ専用 (`collectInputLatency`)。
  private readonly latencyCollectRequests = new Map<
    number,
    (
      entries: ReadonlyArray<{
        readonly first: boolean;
        readonly delayMs: number;
        readonly inputAt: number;
      }>,
    ) => void
  >();
  // 層サムネ (layer-thumbnails-2026-10-13.md)。
  private readonly thumbStatsRequests = new Map<number, (d: readonly number[]) => void>();
  private thumbEnabled = false;
  private thumbState: LayerThumbnailState = 'off';
  private readonly thumbStateListeners = new Set<(s: LayerThumbnailState) => void>();
  private thumbVisible = true;
  private readonly thumbListeners = new Set<(t: LayerThumbnailMessage) => void>();
  /** Latest thumbnail per layer: replayed to a new subscriber, dropped on OFF. */
  private readonly thumbCache = new Map<number, LayerThumbnailMessage>();
  private thumbJank: ThumbnailJankSampler | null = null;
  private readonly perfCollectRequests = new Map<
    number,
    (
      entries: ReadonlyArray<{
        readonly name: string;
        readonly startTime: number;
        readonly duration: number;
      }>,
    ) => void
  >();
  //
  // ─── 2026-05-23 — remote backpressure: coalesce, not drop ─────────────
  //
  // `remoteCoalesce` is the ordered, coalescing buffer for **remote** peer
  // broadcasts bound for the worker (docs/_wip/backpressure-coalesce.md).
  // The old per-frame gate silent-dropped overflow `stroke.points`, which
  // corrupted received curves into chords (a dropped middle segment makes
  // the renderer connect the survivors with a straight line). The gate's
  // real job is only to bound postMessage *count/size*, which coalescing
  // achieves losslessly: same-sid `stroke.points` are concatenated by the
  // renderer, so merging their `points` arrays renders identically.
  //
  // Local strokes (`addPoints`) DELIBERATELY bypass this queue and forward
  // immediately: they're already self-rate-limited to ~1 flush/frame by the
  // UI's rAF and can't flood, and decimating one's own line is the worst
  // UX. (This also fixes a latent bug where local points shared the old
  // gate's budget and could be dropped.) The queue is remote-only.
  //
  // The flush is driven by the SAME dual-arm rAF + setTimeout fallback the
  // bg-tab fix introduced: rAF wins in the foreground (~16 ms), the throttled
  // timeout (~1 s) keeps draining in a backgrounded tab. Whichever fires
  // first flushes the queue and cancels the other. In the background, points
  // accumulate (coalesced) and flush in batches instead of being lost, so the
  // curve is fully reconstructed on the next flush / foreground resume.
  private readonly remoteCoalesce: RemoteCoalesceQueue;
  /** rAF handle for the coalesce-queue flush. `-1` while idle. */
  private flushRafHandle: number = -1;
  /**
   * Timeout handle paired with `flushRafHandle` (rAF + timeout 二段, reused
   * from the bg-tab fix). `null` while idle. See `armCoalesceFlush` for why
   * both are armed and why the first to fire cancels the other.
   */
  private flushTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

  // Watchdog timer for §19.7 idle-stroke force-end.
  private idleTimer: ReturnType<typeof setInterval> | null = null;

  // ─── Layer display opacity (layer-display-opacity-2026-10-12.md) ─────────
  /** Confirmed per-layer display opacity, mirrored on the main thread (index = layerId). */
  private readonly layerOpacities: number[] = new Array(CLASSIC_LAYER_COUNT).fill(1);
  /** Active drag preview; `sentValue` = last value shipped to the core (rAF coalesce). */
  private opacityPreview: { layerId: number; value: number; sentValue: number | null } | null =
    null;
  private opacityPreviewFlushScheduled = false;
  /** Active 長押し強調 (layer id), mirrored from the core. */
  private layerSolo: number | null = null;
  private readonly soloListeners = new Set<(layerId: number | null) => void>();
  private readonly opacityListeners = new Set<(opacities: readonly number[]) => void>();

  // ─── Phase 7 Task 7-8a — imagepaste preview state ────────────────────────
  private imagepastePreview: ImagepastePreviewInternal | null = null;
  private readonly imagepasteListeners = new Set<(state: ImagepastePreviewState | null) => void>();
  private imagepasteDrag: { offsetX: number; offsetY: number } | null = null;
  private readonly imagepasteFetchFn: ImagepasteFetchFn;
  private readonly imagepasteCreateBitmapFn: ImagepasteCreateBitmapFn;
  private readonly imagepasteDecodeBitmapFn: ImagepasteDecodeBitmapFn;
  private readonly imagepasteResampleFn: ImagepasteResampleFn;
  private readonly imagepastePngBlobFn: ImagepastePngBlobFn;
  /** Pending coalesced resample (rAF / microtask); see `scheduleImagepasteResample`. */
  private imagepasteResampleScheduled = false;
  // W3 Classic レイヤー追加 (2026-05-26) — RGBA → PNG dataURL encoder for
  // `exportAllLayersBlob` (camera capture, 全層 visibility 無視).
  private readonly pngEncodeFn: PngEncodeFn;

  // ─── Phase 7 Task 7-10a — stroke / imagepaste filter state ───────────────
  //
  // `filterUids` is the authoritative set of uids whose drawing broadcasts
  // are dropped before they reach the renderer. **Future-only**: changing
  // the set never re-renders past pixels (snapshots are raster, so committed
  // strokes can't be retroactively peeled out).
  //
  // `filteredStrokeSids` tracks `stroke.start`s that were skipped, so the
  // matching `stroke.segment` / `stroke.end` for the same `sid` are dropped
  // even if the user removes the uid from `filterUids` mid-stroke. (architecture.md §19.14)
  private filterUids: ReadonlySet<string> = EMPTY_FILTER_SET;
  private readonly filteredStrokeSids = new Set<string>();

  // ─── conflict-aware Undo (C1-C4, docs/_wip/undo-conflict-aware-2026-07-12.md) ─
  //
  // `undoStore` is the client "B' baseline canvas" mirror of the server's
  // session buffer (§19.10 A/E-friendly — pure class, no DOM). It is fed by
  // `ingestUndoBroadcast(Batch)`, which the session layer calls for EVERY
  // incoming `ServerEvent` (self AND remote — deliberately NOT gated by the
  // self-uid echo suppression `routeBroadcastToEngine` applies for the
  // render path, since the B' baseline needs every stroke's geometry
  // regardless of author to compute correct rewinds).
  //
  // Sized to the CURRENT canvas dims + the fixed Classic layer count (the
  // engine always allocates `CLASSIC_LAYER_COUNT` layers, see `bootInline` /
  // `bootWorker`); recreated on `resize()` alongside the full pixel wipe
  // `EngineCore.resize` already performs, so stale pre-resize stroke records
  // never leak into a post-resize rewind.
  private undoStore: UndoBaselineStore;

  /** sids this client originated via `startStroke()` (irrespective of end/cancel state). */
  private readonly ownStrokeSids = new Set<string>();
  /**
   * 自分の線のうち stroke.end の echo がまだ戻っていないもの (sid → 触れた範囲)。
   * `UndoBaselineStore` には echo 済みの点列しか無いので、これらと重なる矩形を
   * 作り直すと手元だけにある線を消してしまう。矩形の作り直しは、重なる間は保留する。
   */
  private readonly ownLiveStrokes = new Map<
    string,
    {
      layerId: number;
      margin: number;
      x0: number;
      y0: number;
      x1: number;
      y1: number;
      since: number;
    }
  >();
  /** decode 済みの貼り付けのうち、seq 順の作り直しを待っているもの。 */
  private pendingRepairs: Array<{
    layerId: number;
    seq: number;
    rect: { x0: number; y0: number; x1: number; y1: number };
    expiresAt: number;
  }> = [];
  /**
   * "cancel 可能な自分の stroke スタック" (C4) — MY strokes that have been
   * server-confirmed ended (echo observed) and not yet cancelled. Push order
   * = end-confirmation order; `getCancellableOps()` reverses so index 0
   * is the most recently ended (LIFO 提示順 — a UI presentation ordering
   * ONLY, per 2026-07-22 「LIFO はサーバ強制しない」: the server is the sole
   * arbiter of whether a given `sid` is actually cancellable right now).
   */
  private readonly cancellableStack: CancellableOp[] = [];
  /**
   * 自分が範囲の移動・コピーを送った uid (`holdOwnStrokeInput({ releaseOnRegionFromUid })` で
   * 記録)。この uid の `whiteboard.region` の echo だけを「自分の取り消せる操作」に積む
   * (線の `ownStrokeSids` と同じ「このセッションで自分が出したものだけ」の考え方。リロード後の
   * 過去の移動は積まない)。
   */
  private ownRegionUid: string | null = null;
  /**
   * 自分が確定で送る範囲の絵 (srcLayerId → 写し取った絵)。自分の echo ではこれをそのまま置き、
   * R2 からの fetch+decode を待たない (待つと元は消えたのに置き先に何もない空白が出る)。
   * 確定が失敗/終了して保持を外すとき (`releaseOwnStrokeInput`) に捨てる。
   */
  private ownRegionImages: Map<number, RegionImage> | null = null;
  /**
   * 取り消された `whiteboard.region` の seq。画像の fetch+decode が取り消しより後に終わったとき
   * に適用しないための印 (store 側は bake で記録を落とすので、engine 側にも持つ)。
   */
  private readonly cancelledRegionSeqs = new Set<number>();
  private readonly cancellableListeners = new Set<(stack: readonly CancellableOp[]) => void>();
  private readonly cancelRejectionListeners = new Set<(payload: UndoCancelRejection) => void>();
  /** Coalescing (f4): batches same-tick `stroke.cancelled`s into one rewind pass. */
  private undoRewindFlushScheduled = false;
  /** Time-sliced bake loop driver (f4 "同期一括 bake しない"). */
  private bakeScheduled = false;

  // ─── Phase 6.9 — name labels ─────────────────────────────────────────────
  //
  // **Per-uid 1-label, real-time tracking** (Phase 6.9 仕様変更 2026-05-06):
  //   `nameLabels` is keyed by `uid` and holds at most one label per user.
  //   Every `stroke.start` and `stroke.segment` from a given `uid` overwrites
  //   the label at that key with the latest `(x, y)` so the label "follows"
  //   the peer's brush in real time. The visual effect is
  //   「他人が描いている最中もネームがついてくる」/「一緒に描いている感」.
  //
  // **Why real-time on segment** (revised from earlier "on stroke.end" model):
  //   The Phase 6.9 first cut only repositioned the label on stroke.end so
  //   the label "jumped" once per stroke. User feedback (2026-05-06) was that
  //   this loses the "I see the other person actively drawing" cue —
  //   especially during long strokes. Updating on every segment broadcast
  //   gives proper presence at the cost of an extra listener fire per
  //   segment (~5–20 Hz per active peer). React's diff handles `<span>`
  //   position updates cheaply; throttling can be added later if profiles
  //   show jank under many concurrent peers.
  //
  // **Pending state**:
  //   `pendingStrokes` keys by `sid` and holds the in-flight stroke metadata
  //   plus the running `lastX/Y`. Initialized on `stroke.start` (no x/y in
  //   protocol → `lastX/Y` start as `null` until the first segment arrives).
  //   Every `stroke.segment` advances `lastX/Y` to the final point of that
  //   segment AND mirrors that point into `nameLabels`. `stroke.end` simply
  //   deletes the pending entry — it does NOT touch `nameLabels` (the most
  //   recent segment already left the label at the final position).
  //
  // **stroke.start initial position**:
  //   The protocol does not carry start-point coordinates (the first segment
  //   does). We therefore can't seed `nameLabels` on stroke.start with a
  //   meaningful `(x, y)` for *brand-new* strokes. For a uid that already
  //   has a committed label from a prior stroke, the existing label stays
  //   put until the first segment of the new stroke arrives — at which
  //   point it jumps to the live brush position. This means a zero-point
  //   tap (start → end with no segment) is a no-op for `nameLabels`,
  //   leaving any prior label intact (regression-tested).
  //
  // **De-dup**:
  //   No `(uid, seq)` ledger — repeated stroke.start / segment / end
  //   broadcasts (replay / resume) re-seed the same `pendingStrokes[sid]`
  //   idempotently and mirror identical `(x, y)` into `nameLabels`. The
  //   listener-fire path skips notifications when the new value is
  //   identical to the previous one to keep notifications meaningful.
  //   Same uid drawing multiple strokes intentionally **overwrites** the
  //   label slot — the latest stroke wins.
  //
  // **TTL auto-expire** (Phase 6.9 仕様追加 2026-05-06, legacy 踏襲):
  //   Each label carries a `birthtime` (epoch ms) refreshed on every
  //   `advancePendingStroke` that produces a *new* `(x, y, name, seq)` tuple.
  //   A dedicated `expireTimer` ticks every `EXPIRE_TICK_MS` (100 ms) and
  //   evicts labels whose `birthtime` is older than `NAME_LABEL_TTL_MS`
  //   (1 s). Pen-still-but-still-drawing (= same-tail-point segments) does
  //   NOT refresh `birthtime` — that is intentional: 旧版 the label had no
  //   refresh signal beyond actual movement, so 「ペン静止で 1 秒経過 →
  //   消える」 is the documented behaviour we replicate. Worst-case expire
  //   delay is one tick (100 ms) on top of the TTL, well below UX threshold.
  //
  // `nameLabelMode` is just a visibility toggle the UI consumes; the engine
  // still maintains the underlying state regardless of mode.
  //
  // `nameLabelListeners` mirrors the imagepaste listener pattern so the React
  // layer can render an HTML overlay that re-positions on every mutation.
  private readonly nameLabels = new Map<string, NameLabel>();
  private readonly pendingStrokes = new Map<
    string,
    { name: string; uid: string; seq: number; lastX: number | null; lastY: number | null }
  >();
  // 2026-05-15 ユーザ実機 fb で **default ON** (= true) に変更. 「誰がどこに描いてるか」
  // のライブ感が新版の差別化軸 (competitive-landscape.md §4) なので、初期表示で
  // 名前が見えてる方が UX として自然. UI 側 (ClassicLayout) の initial useState も合わせる.
  private nameLabelMode = true;
  private readonly nameLabelListeners = new Set<
    (state: { mode: boolean; labels: ReadonlyArray<NameLabel> }) => void
  >();
  // 100 ms cadence — fast enough that worst-case eviction lag is invisible
  // (TTL 1000 ms ± 100 ms), cheap enough to ignore: the per-tick body is
  // `nameLabels.size === 0 ? noop : Map.iterate + delete + listener fire`.
  private expireTimer: ReturnType<typeof setInterval> | null = null;

  // ─── Phase 6.9 — zoom ────────────────────────────────────────────────────
  //
  // CSS-only display zoom (architecture.md §Phase 6.9 / implementation-plan.md):
  // the canvas pixel buffer stays at its native resolution; the engine
  // applies `transform: scale(zoom) + transform-origin: 0 0` and
  // `image-rendering: pixelated` directly to the DOM canvas so the React
  // layer can call `setZoom` without manually touching the style attribute.
  //
  // Pointer-event coordinate inversion happens upstream in DrawingCanvas via
  // the `CanvasViewMapper` (`viewport.ts` §1b — one transform for zoom / pan /
  // flip / rotation), so the engine's only zoom job is to mutate the canvas
  // style + notify.
  private zoom = ZOOM_MIN;
  private readonly zoomListeners = new Set<(zoom: number) => void>();

  // ─── ドットバイドット表示 (2026-10-28, docs/_wip/canvas-dot-by-dot-2026-10-28.md) ──
  //
  // dpr が (1, 2) のとき、表示倍率 (CSS px / canvas px) を `zoom / dpr` にして
  // ズーム 1x で 1 canvas px = 1 画面ドットにする。`zoom` (利用者の倍率) は不変、
  // CSS 幾何に使うのは `getDisplayScale()`。dpr はモニター移動・ブラウザズームで
  // 変わるので matchMedia(resolution) の change で追従する。
  private dotByDotMode: DotByDotMode = DEFAULT_DOT_BY_DOT_MODE;
  private dpr = 1;
  private dprUnwatch: (() => void) | null = null;
  private readonly displayScaleListeners = new Set<(scale: number) => void>();

  // ─── canvas-flip (view flip, 2026-06-07) ────────────────────────────────
  //
  // CSS-only horizontal mirror of the *display* (docs/_wip/canvas-flip.md).
  // Like `zoom`, this never touches pixel data or stroke coordinates — the
  // engine always records strokes in true canvas space. `applyZoomStyle`
  // composes the flip into the same `transform` string as zoom (negative
  // scaleX + a translateX correction so the mirrored canvas stays in box).
  //
  // Pointer-event coordinate un-flip happens upstream in DrawingCanvas via the
  // `CanvasViewMapper` (`viewport.ts` §1b), same as zoom's inverse. The
  // engine's only flip job is to mutate the canvas style + notify subscribers
  // (zoom と同型).
  private flipX = false;
  private readonly flipListeners = new Set<(flip: boolean) => void>();

  constructor(canvas: HTMLCanvasElement, opts: CanvasDrawingEngineOptions = {}) {
    this.canvas = canvas;
    if (opts.dotByDotMode !== undefined) this.dotByDotMode = opts.dotByDotMode;
    this.startWatchingDpr();
    // An explicit `opts.pointFilter` (tests / custom filters) takes full
    // ownership — the engine won't recreate it on `setStabilizer`. Otherwise
    // the engine owns a level-driven filter, default `'off'` (= identity).
    if (opts.pointFilter !== undefined) {
      this.pointFilter = opts.pointFilter;
      this.stabilizerLevel = null;
      this.pendingStabilizerLevel = null;
    } else {
      this.stabilizerLevel = 'off';
      this.pendingStabilizerLevel = 'off';
      this.pointFilter = createIdentityPointFilter();
    }
    this.idleStrokeTimeoutMs = opts.idleStrokeTimeoutMs ?? 30_000;
    // Remote coalesce queue forwards via `forwardToWorker` (= the raw worker
    // postMessage path, NOT `send`): the queue IS the backpressure mechanism,
    // so re-entering `send` would be pointless. The cap defaults to the
    // memory-safety net; tests inject a small cap to exercise decimation.
    this.remoteCoalesce = new RemoteCoalesceQueue(
      (msg) => this.forwardToWorker(msg),
      opts.remotePointCap,
    );
    this.imagepasteFetchFn = opts.imagepasteFetchFn ?? defaultImagepasteFetch;
    this.imagepasteCreateBitmapFn = opts.imagepasteCreateBitmapFn ?? defaultImagepasteCreateBitmap;
    this.imagepasteDecodeBitmapFn = opts.imagepasteDecodeBitmapFn ?? defaultImagepasteDecodeBitmap;
    this.imagepasteResampleFn = opts.imagepasteResampleFn ?? resampleBitmapToRgba;
    this.imagepastePngBlobFn = opts.imagepastePngBlobFn ?? encodeRgbaToPngBlob;
    this.regionPngDecodeFn = opts.regionPngDecodeFn ?? ((bytes) => decodeRgbaPng(bytes));
    this.pngEncodeFn = opts.pngEncodeFn ?? defaultPngEncode;

    // Fallback when the canvas attribute is unset (caller should always
    // pass through the room.welcome canvas dimensions, but we keep a
    // sensible default that matches the new server default = 1500×1500).
    const initialW = canvas.width || 1500;
    const initialH = canvas.height || 1500;
    if (canvas.width === 0) canvas.width = initialW;
    if (canvas.height === 0) canvas.height = initialH;
    const background = opts.background;

    // conflict-aware Undo (C1) — sized to match the layer stack `bootInline`
    // / `bootWorker` allocate below (`CLASSIC_LAYER_COUNT`).
    this.undoStore = this.makeUndoBaselineStore(initialW, initialH);

    const useWorker =
      !opts.forceInline && opts.workerFactory !== undefined && canSupportOffscreenWorker(canvas);

    if (useWorker && opts.workerFactory) {
      try {
        this.bootWorker(opts.workerFactory, canvas, initialW, initialH, background);
      } catch (err) {
        // Worker boot failed — fall back to inline silently. The main
        // thread renderer is correctness-equivalent.
        // eslint-disable-next-line no-console
        console.warn('[CanvasDrawingEngine] worker boot failed, falling back to inline', err);
        this.bootInline(canvas, initialW, initialH, background);
      }
    } else {
      this.bootInline(canvas, initialW, initialH, background);
    }

    this.startIdleWatchdog();
    this.startNameLabelExpireTimer();

    // PR 2-β follow-up (2026-05-21) — dev-only engine registry for the
    // perf-harness bridge in `main.tsx`. The harness needs an engine
    // handle to route `__OEKAKI_PERF__.enableDrawingPerfMeasure()` into
    // the worker (= where the hot path lives). The registry pattern keeps
    // CanvasDrawingEngine ignorant of the harness wiring — main.tsx reads
    // `window.__OEKAKI_ENGINES__` lazily.
    //
    // `import.meta.env.DEV` is a Vite bundler-time const → dropped from
    // production bundles via DCE. Cost in production = 0.
    if (import.meta.env.DEV) {
      registerEngineForDev(this);
    }
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  startStroke(input: StartStrokeInput): { sid: string } {
    this.guardLive();
    // My pen going down ends 長押し強調 (the core does the same on `own`).
    this.dropLayerSoloLocal();
    const sid = generateSid();
    this.active.add(sid);
    // conflict-aware Undo (C4) — remember this sid is mine so the eventual
    // `stroke.end` broadcast echo (observed via `ingestUndoBroadcast`) can
    // push it onto the cancellable stack.
    this.ownStrokeSids.add(sid);
    this.trackOwnLiveStart(sid, input.layerId, input.width);
    // Apply any pending stabiliser-level change here (at stroke boundary) so
    // the strength never shifts mid-stroke (§9.8). Only when the engine owns
    // the filter (no explicit `opts.pointFilter` override).
    if (
      this.stabilizerLevel !== null &&
      this.pendingStabilizerLevel !== null &&
      this.pendingStabilizerLevel !== this.stabilizerLevel
    ) {
      this.stabilizerLevel = this.pendingStabilizerLevel;
      this.pointFilter = createPointFilterForLevel(this.stabilizerLevel);
    }
    this.pointFilter.reset();
    const nowMs = Date.now();
    this.send({
      t: 'stroke.start',
      sid,
      pen: input.pen,
      color: input.color,
      alpha: input.alpha,
      width: input.width,
      layerId: input.layerId,
      mask: input.mask,
      aa: input.aa ?? false,
      // texture preset (texture-brush-design.md §11 step 3). Omitted for
      // non-texture pens (back-compat) — `penParamsFor` returns undefined.
      ...(input.penParams !== undefined ? { penParams: input.penParams } : {}),
      nowMs,
      // Layer thumbnails: nothing runs while my own pen is down.
      own: true,
    });
    return { sid };
  }

  /**
   * Feed raw pointer samples for an active local stroke.
   *
   * Returns the **filtered** points (after `pointFilter.push`) so the caller
   * (`DrawingCanvas`) can forward *those same points* over the transport
   * instead of the raw input — this is the §9.8 fix that keeps local render,
   * peers, and the server snapshot on one identical (stabilised) curve. With
   * the identity filter (= stabiliser `off`) the return value equals the
   * input, so the transport wiring is bit-identical to the pre-filter
   * behaviour (Classic / Modern-off no-op).
   *
   * Local rendering (`this.send` → worker / inline) is unchanged.
   */
  addPoints(sid: string, points: ReadonlyArray<StrokePoint>): readonly StrokePoint[] {
    this.guardLive();
    if (!this.active.has(sid)) return EMPTY_POINTS;
    if (points.length === 0) return EMPTY_POINTS;
    const filtered: StrokePoint[] = [];
    for (const pt of points) {
      filtered.push(...this.pointFilter.push(pt));
    }
    if (filtered.length === 0) return EMPTY_POINTS;
    this.trackOwnLivePoints(sid, filtered);
    this.send({
      t: 'stroke.points',
      sid,
      points: filtered,
      nowMs: Date.now(),
      // 描き味ベンチ専用 (inputLatencyProbe.ts): デフォルト OFF = boolean 読み 1 個。
      ...(isInputLatencyEnabled() ? { inputMarks: takeInputLatencyMarks() } : {}),
    });
    return filtered;
  }

  /**
   * Inject a single hold-to-build tick point for `pen='airbrush'`
   * (airbrush-design-2026-08-15.md §3.3). Caller: `DrawingCanvas`, driven by a
   * 50ms pointermove-silence timer (§3.4), NOT by `addPoints`.
   *
   * **Deliberately bypasses `this.pointFilter.push`** — unlike `addPoints`.
   * The stabiliser (`PointFilter.ts` weak/medium/strong) unconditionally drops
   * zero-length segments (`if (segLen === 0) return [];`), so a stationary
   * hold-to-build point routed through the normal filter path would emit
   * NOTHING while stabilisation is on, silently disabling hold-to-build for
   * every Modern user who has it enabled (§3.3 is explicit that this is the
   * failure mode to avoid). Skipping the filter here is what makes tick
   * points reach the wire regardless of stabiliser level.
   *
   * Mirrors `addPoints`'s dispatch (`this.send` → worker / inline core) so the
   * point is rendered locally through the exact same path, and returns the
   * point verbatim so the caller forwards *this same point* over the
   * transport (`sendStrokeSegment`) — preserving the local-render == wire-point
   * invariant the airbrush hash's determinism depends on (design §1.2 / §3.2:
   * the hash's `pointIndex` counts consumed wire points, so local render and
   * the wire MUST stay on the identical point sequence).
   */
  injectHoldTickPoint(sid: string, point: StrokePoint): readonly StrokePoint[] {
    this.guardLive();
    if (!this.active.has(sid)) return EMPTY_POINTS;
    this.send({ t: 'stroke.points', sid, points: [point], nowMs: Date.now() });
    return [point];
  }

  /**
   * End an active local stroke.
   *
   * Returns the **flush tail** (`pointFilter.flush`) so the caller can send
   * those points over the transport *before* `stroke.end` — the stabiliser's
   * convergence tail (§9.8) must reach peers / the snapshot, not just the
   * local canvas. With the identity filter the tail is empty, so the wiring
   * is bit-identical to the pre-filter behaviour.
   *
   * Idempotent: §19.7 watchdog / disconnect cleanup may force-end strokes the
   * engine has already cleaned up; a redundant call returns `[]`.
   */
  endStroke(sid: string): readonly StrokePoint[] {
    this.guardLive();
    if (!this.active.delete(sid)) {
      // Idempotent: §19.7 watchdog / disconnect cleanup may force-end
      // strokes the engine has already cleaned up. Don't warn.
      return EMPTY_POINTS;
    }
    const tail = this.pointFilter.flush();
    if (tail.length > 0) {
      this.send({ t: 'stroke.points', sid, points: tail, nowMs: Date.now() });
    }
    this.send({ t: 'stroke.end', sid });
    this.resolveOwnStrokeIdleWaiters();
    return tail;
  }

  /**
   * Set the stroke-stabilisation level (手ぶれ補正, modern-layout.md §9.8).
   *
   * The engine is shared & persisted across layout switches (§9.7), so a
   * single call here affects every subsequent stroke regardless of layout.
   * The change is **deferred to the next stroke's `startStroke`** so the
   * smoothing strength never shifts mid-stroke. `'off'` restores the identity
   * filter (bit-identical to pre-stabiliser behaviour).
   *
   * No-op when an explicit `opts.pointFilter` was injected (tests / custom
   * filters own the filter).
   */
  setStabilizer(level: StabilizerLevel): void {
    if (this.stabilizerLevel === null) return; // explicit filter override owns it
    this.pendingStabilizerLevel = level;
  }

  /** Current (active) stabiliser level, or `null` when a custom filter is injected. */
  getStabilizer(): StabilizerLevel | null {
    return this.stabilizerLevel;
  }

  /**
   * Set the 表示専用リード線 (display-only lead, modern-layout.md §9.8.1 ①).
   *
   * The renderer rasterizes the short gap「確定平滑点 → (生入力 …) → カーソル」with
   * the SAME stamp machinery the committed stroke uses (`drawDot`/`drawLine` →
   * `stampOnce`/`stampOnceAA`) into a transient buffer, and shows it as a
   * frontmost display-only overlay. The lead's appearance (太さ/濃さ/AA/エッジ)
   * therefore matches the body that will form behind it.
   *
   * **Determinism / 送信不変 (絶対)**: this dispatches ONLY into the renderer
   * (`this.send` → worker IPC / inline core). It NEVER calls `transport` —
   * `addPoints` / `endStroke` / the WS send path are untouched, so the sent
   * point list is bit-identical with or without the lead. The lead is
   * uncommitted + unsent; canonical geometry stays the smoothed curve.
   *
   * `pen` carries the current active stroke's color / alpha / width / aa. Keep
   * `points` short (the gap only) so the per-frame rasterize cost is bounded
   * (= 負荷2倍回避; the smoothed body is never re-rasterized).
   *
   * No-op-equivalent inputs (eraser pen / empty points) clear the lead in the
   * core (= ゼロ長リードは非表示, Classic / 補正 OFF で no-op)。
   */
  setLead(args: {
    pen: LeadPenMetaMsg;
    layerId: number;
    mask?: StrokeMask;
    points: readonly StrokePoint[];
  }): void {
    this.guardLive();
    this.send({
      t: 'lead.set',
      pen: args.pen,
      layerId: args.layerId,
      ...(args.mask !== undefined ? { mask: args.mask } : {}),
      points: args.points,
    });
  }

  /** Clear the display-only lead (modern-layout.md §9.8.1 ①). Fired on stroke end / cancel. */
  clearLead(): void {
    if (this.disposed) return; // idempotent on a torn-down engine (end-of-stroke race)
    this.send({ t: 'lead.clear' });
  }

  /**
   * Apply a list of server-broadcast events to the local layers. Used by:
   *   - `room.welcome.deltaEvents` after the snapshot loaded
   *   - `room.resume-ok.events` (delta resume)
   *   - any time a remote stroke broadcast needs to be replayed locally
   *
   * Accepts `unknown[]` because the wire types are intentionally loose
   * (protocol-events.md §2.10) — we validate each item against the right
   * `*BroadcastSchema` and skip anything that doesn't match. Unknown
   * shapes are silently dropped so the engine is forward-compatible with
   * future event kinds.
   */
  applyRemoteStroke(events: ReadonlyArray<unknown>): void {
    this.guardLive();
    for (const ev of events) {
      // Try each schema in turn. zod safeParse is cheap because the
      // discriminator branches reject early.
      const start = strokeStartBroadcastSchema.safeParse(ev);
      if (start.success) {
        // Phase 7 Task 7-10a — future-only filter. Skip the start AND
        // remember the sid so the trailing segment/end are dropped too,
        // even if the user un-filters this uid mid-stroke. (architecture.md §19.14)
        if (this.filterUids.has(start.data.uid)) {
          this.filteredStrokeSids.add(start.data.sid);
          continue;
        }
        // Phase 6.9 — open a pending stroke entry keyed by sid. Real-time
        // tracking: each subsequent stroke.segment mirrors its tail point
        // into nameLabels[uid] so the label follows the brush. The protocol
        // has no start-point coords, so we don't touch nameLabels here; if
        // no segment ever arrives, any prior label for this uid stays put.
        this.openPendingStroke(start.data.sid, {
          name: start.data.displayName,
          uid: start.data.uid,
          seq: start.data.seq,
        });
        this.sendRemote({
          t: 'stroke.start',
          sid: start.data.sid,
          pen: start.data.pen,
          color: start.data.color,
          alpha: start.data.alpha,
          width: start.data.width,
          layerId: start.data.layerId,
          mask: start.data.mask ?? { type: 'none', colors: [] },
          // modern-layout.md §9.9 — forward the originator's AA flag so remote
          // peers render the stroke with the same coverage as the source.
          // `?? false` is belt-and-braces; the broadcast schema defaults it.
          aa: start.data.aa ?? false,
          // texture-brush-design.md §11 step 3 — forward the originator's
          // texture preset so remote peers AND late-join replay
          // (room.welcome.deltaEvents / resume events all route through
          // applyRemoteStroke) render texture, not a plain brush. Without this
          // the local startStroke path carried penParams but remote/replay
          // dropped it → texture strokes rendered as brush on other clients
          // and on re-entry (2026-06-19 bug). Omitted ⇒ EngineCore falls back
          // to 紙 default (back-compat with non-texture pens).
          ...(start.data.penParams !== undefined ? { penParams: start.data.penParams } : {}),
          nowMs: start.data.ts,
        });
        continue;
      }
      const seg = strokeSegmentBroadcastSchema.safeParse(ev);
      if (seg.success) {
        if (this.filteredStrokeSids.has(seg.data.sid)) continue;
        // Phase 6.9 — real-time tracking: mirror the segment's tail point
        // into nameLabels[uid] so the label follows the live brush. We
        // pick the *final* point of the segment (rather than the first or
        // some midpoint) to minimize listener fires while keeping the
        // label visually leading-edge with the stroke. Idempotent when no
        // pending entry exists (e.g. start was filtered upstream).
        const last = seg.data.pts[seg.data.pts.length - 1];
        if (last !== undefined) {
          this.advancePendingStroke(seg.data.sid, last.x, last.y);
        }
        this.sendRemote({
          t: 'stroke.points',
          sid: seg.data.sid,
          points: seg.data.pts,
          nowMs: seg.data.ts,
        });
        continue;
      }
      const end = strokeEndBroadcastSchema.safeParse(ev);
      if (end.success) {
        if (this.filteredStrokeSids.delete(end.data.sid)) {
          // Filter-tracked stroke completed — discard, no commit.
          continue;
        }
        // Phase 6.9 — drain the pending entry. Real-time tracking already
        // mirrored the stroke's last segment tail into nameLabels[uid] on
        // the most recent stroke.segment, so closing is pure cleanup —
        // nameLabels is intentionally NOT touched here. A zero-point tap
        // (start → end with no segment) leaves any prior label intact,
        // matching the legacy "describing 描き終わった位置" semantics.
        this.closePendingStroke(end.data.sid);
        this.sendRemote({ t: 'stroke.end', sid: end.data.sid });
        continue;
      }
      const clear = whiteboardClearBroadcastSchema.safeParse(ev);
      if (clear.success) {
        // whiteboard.clear is uniform pub/sub: it affects the shared
        // canvas, so per-uid filtering doesn't apply. (architecture.md §19.14)
        // Phase 6.9 — clear wipes name labels too (legacy踏襲): the visual
        // tie-down between label and underlying stroke pixels is gone after
        // clear, so dangling labels would just be visual noise.
        this.clearNameLabels();
        this.sendRemote({ t: 'clear', layerId: clear.data.layerId });
        continue;
      }
      const imagepaste = whiteboardImagepasteSchema.safeParse(ev);
      if (imagepaste.success) {
        // Phase 7 Task 7-10a — imagepaste is "a kind of drawing"; filter
        // by originator uid, same rule as stroke.* (protocol-events.md §5.1).
        if (this.filterUids.has(imagepaste.data.uid)) continue;
        // Fire-and-forget: fetch + decode + commit are async, but
        // applyRemoteStroke is synchronous (and shared with stroke replay
        // which is synchronous). Failures are swallowed with a console.warn
        // so the rest of the replay continues. (Phase 7 Task 7-8a)
        void this.fetchAndApplyImagepaste(imagepaste.data);
        continue;
      }
      // 範囲の移動・コピー (`whiteboard.region`、仮の型 — regionBroadcast.ts)。自分の echo も
      // この経路で適用する (EngineBroadcastRouter の SKIP_ECHO_SUPPRESSION)。
      const regionEv = parseRegionBroadcast(ev);
      if (regionEv !== null) {
        if (this.filterUids.has(regionEv.uid)) {
          this.releaseOwnStrokeHoldFor(regionEv.uid);
          continue;
        }
        void this.fetchAndApplyRegion(regionEv);
      }
      // Unknown event kind — ignore.
    }
  }

  // ─── Phase 7 Task 7-10a — stroke filter API ──────────────────────────────

  /**
   * Replace the stroke filter set. From this point on, `stroke.*` and
   * `whiteboard.imagepaste` broadcasts whose `uid` is in `uids` are
   * dropped before they reach the renderer.
   *
   * **Future-only** (architecture.md §19.14):
   *   - Pixels already committed to `Layer.main` (= rendered before the
   *     filter was set, or replayed from a snapshot) are NOT removed.
   *     Removing them would require keeping per-uid layer history, which
   *     fights the snapshot-as-raster invariant.
   *   - A stroke whose `stroke.start` was skipped continues to be skipped
   *     for the rest of its lifetime (segment/end), even if the user
   *     removes that uid from the filter mid-stroke. The complementary
   *     direction — adding a uid to the filter while a stroke is in
   *     flight — lets the in-flight stroke finish (start was already
   *     committed, partially erasing it would look like a glitch). New
   *     strokes from that uid are skipped from then on.
   *   - `whiteboard.clear` is intentionally NOT filtered — it's a shared
   *     canvas operation, not a per-uid drawing event.
   *
   * The caller passes a `Set<string>` (or any `ReadonlySet`); the engine
   * stores its own copy so subsequent mutations on the caller's side don't
   * affect the engine state.
   *
   * Self-uid is NOT specially excluded here: if the caller chooses to
   * include their own uid, this API will gate the broadcasts of their own
   * strokes / imagepaste. That breaks the imagepaste preview-as-ack flow
   * (the originator's own broadcast is what triggers the local commit
   * after upload). The UI layer (Task 7-10b) is expected to never let the
   * user filter themselves; the engine doesn't second-guess it.
   */
  setStrokeFilter(uids: ReadonlySet<string>): void {
    this.guardLive();
    // Defensive copy — store as our own immutable set.
    this.filterUids = new Set(uids);
    // Mirror to the worker so the contract stays consistent if the gate
    // ever moves into the worker. The worker is currently a no-op consumer.
    this.send({ t: 'filter.set', uids: Array.from(this.filterUids) });
  }

  /**
   * Read the current stroke filter set. Mainly for tests; callers that
   * need to subscribe to changes can keep the Set on their own side and
   * pass a fresh one into `setStrokeFilter`.
   */
  getStrokeFilter(): ReadonlySet<string> {
    return this.filterUids;
  }

  // ─── conflict-aware Undo API (C1-C4) ─────────────────────────────────────

  /**
   * Feed a single incoming `ServerEvent` into the B' baseline mechanism
   * (`UndoBaselineStore`). The session layer should call this for EVERY
   * event received over the transport, in ADDITION to (not instead of) the
   * existing render-path routing (`routeBroadcastToEngine` /
   * `EngineBroadcastRouter`) — this method is deliberately NOT gated by
   * self-uid echo suppression, because the B' baseline needs full stroke
   * geometry regardless of who authored it (a rewind may need to replay
   * ANY user's intersecting strokes, including this client's own).
   *
   * Handles: `stroke.start` / `stroke.segment` / `stroke.end` (ingested into
   * the store; unrelated shapes are silently ignored — safe to call with any
   * `ServerEvent`), `stroke.cancelled` (tombstone → queues + schedules a
   * coalesced bbox-local rewind), `snapshot.published` (advances the bake
   * target + kicks off the time-sliced bake loop), `whiteboard.imagepaste`
   * (metadata only — `onImagepaste`, synchronously; the decoded bitmap
   * arrives separately once `fetchAndApplyImagepaste`'s own fetch+decode
   * resolves, see `onImagepasteBitmap`'s doc for why the split matters),
   * `whiteboard.clear` (`onClear`, per-layer or full — 2026-07-23「第二弾
   * 設計確定」imagepaste/per-layer-clear × Undo 穴埋め). Everything else is a
   * no-op.
   */
  ingestUndoBroadcast(event: unknown): void {
    this.guardLive();
    const start = strokeStartBroadcastSchema.safeParse(event);
    if (start.success) {
      this.undoStore.onStrokeStart({
        sid: start.data.sid,
        uid: start.data.uid,
        layerId: start.data.layerId,
        pen: start.data.pen,
        color: start.data.color,
        alpha: start.data.alpha,
        width: start.data.width,
        mask: start.data.mask ?? { type: 'none', colors: [] },
        aa: start.data.aa ?? false,
        ...(start.data.penParams !== undefined ? { penParams: start.data.penParams } : {}),
        startSeq: start.data.seq,
      });
      return;
    }
    const seg = strokeSegmentBroadcastSchema.safeParse(event);
    if (seg.success) {
      this.undoStore.onStrokeSegment(seg.data.sid, seg.data.pts);
      return;
    }
    const end = strokeEndBroadcastSchema.safeParse(event);
    if (end.success) {
      this.undoStore.onStrokeEnd(end.data.sid, end.data.seq);
      // C4 — "cancel 可能な自分の stroke スタック": push once the server has
      // confirmed (echoed) the end of one of MY strokes.
      if (this.ownStrokeSids.has(end.data.sid)) {
        this.pushCancellable({ kind: 'stroke', sid: end.data.sid });
      }
      if (this.ownLiveStrokes.delete(end.data.sid)) this.tryPendingRepairs();
      return;
    }
    const cancelled = strokeCancelledBroadcastSchema.safeParse(event);
    if (cancelled.success) {
      this.undoStore.onCancelled(cancelled.data.sid);
      this.scheduleUndoRewindFlush();
      this.removeCancellable({ kind: 'stroke', sid: cancelled.data.sid });
      this.ownStrokeSids.delete(cancelled.data.sid);
      if (this.ownLiveStrokes.delete(cancelled.data.sid)) this.tryPendingRepairs();
      return;
    }
    const published = snapshotPublishedSchema.safeParse(event);
    if (published.success) {
      this.undoStore.onSnapshotPublished(published.data.snapshotSeq);
      this.scheduleBakeStep();
      return;
    }
    // 2026-07-23「第二弾設計確定」imagepaste × Undo 穴埋め — metadata only,
    // synchronously, regardless of `filterUids` (mirrors stroke.* above: the
    // B' baseline needs full raster geometry no matter who authored it, or
    // whether the local render path is currently filtering this uid out).
    // The decoded bitmap lands later via `onImagepasteBitmap`, called from
    // `fetchAndApplyImagepaste` once ITS OWN fetch+decode (kicked off by the
    // separate render-path routing, not from here) settles.
    const imagepaste = whiteboardImagepasteSchema.safeParse(event);
    if (imagepaste.success) {
      this.undoStore.onImagepaste({
        layerId: imagepaste.data.layerId,
        x: imagepaste.data.x,
        y: imagepaste.data.y,
        width: imagepaste.data.width,
        height: imagepaste.data.height,
        seq: imagepaste.data.seq,
      });
      return;
    }
    // 範囲の移動・コピー: メタ (src/dst/items) だけ同期で取り込む。画像は
    // `fetchAndApplyRegion` が fetch+decode したあとに `onRegionImages` で付ける。
    const regionEv = parseRegionBroadcast(event);
    if (regionEv !== null) {
      this.undoStore.onRegion({
        seq: regionEv.seq,
        mode: regionEv.mode,
        src: regionEv.src,
        dst: regionEv.dst,
        items: regionEv.items.map((i) => ({
          srcLayerId: i.srcLayerId,
          dstLayerId: i.dstLayerId,
        })),
      });
      // 自分の移動・コピーなら「元に戻す」の一覧に積む (線の stroke.end echo と同じ位置づけ)。
      if (this.ownRegionUid !== null && regionEv.uid === this.ownRegionUid) {
        this.pushCancellable({ kind: 'region', regionSeq: regionEv.seq });
      }
      return;
    }
    // 範囲の移動・コピーの取り消し (tombstone)。その移動を除いて元と置き先の矩形 (全対象層) を
    // 作り直す (線の stroke.cancelled と同じく同 tick の取り消しは 1 パスにまとめる)。未知 seq は無視。
    const regionCancelled = whiteboardRegionCancelledSchema.safeParse(event);
    if (regionCancelled.success) {
      const regionSeq = regionCancelled.data.regionSeq;
      this.cancelledRegionSeqs.add(regionSeq);
      this.undoStore.onRegionCancelled(regionSeq);
      this.scheduleUndoRewindFlush();
      this.pendingRepairs = this.pendingRepairs.filter((r) => r.seq !== regionSeq);
      this.removeCancellable({ kind: 'region', regionSeq });
      return;
    }
    // whiteboard.clear (per-layer OR full — `layerId` undefined = full,
    // mirrors the broadcast's own field, 2026-07-23「第二弾設計確定」).
    const clear = whiteboardClearBroadcastSchema.safeParse(event);
    if (clear.success) {
      this.undoStore.onClear(clear.data.layerId, clear.data.seq);
    }
  }

  /**
   * Batch counterpart of `ingestUndoBroadcast`, for `room.welcome.deltaEvents`
   * / `room.resume-ok.events`. Feeds every event in `events` (in array
   * order), then drains any queued rewinds ONCE at the end (f4 coalescing —
   * a resume/welcome batch can legitimately contain several
   * `stroke.cancelled`s, e.g. a reconnecting client's resume delta) rather
   * than per-event.
   */
  ingestUndoBroadcastBatch(events: ReadonlyArray<unknown>): void {
    this.guardLive();
    for (const ev of events) this.ingestUndoBroadcast(ev);
    // `ingestUndoBroadcast` already scheduled a microtask flush per cancel
    // seen above; draining synchronously now (in addition) means the batch's
    // rewinds land before this call returns rather than on the next
    // microtask tick, which matters for late-join / resume where the caller
    // wants the canvas fully settled before showing it.
    this.applyPendingUndoRewinds();
  }

  /**
   * Seed the B' baseline from an already-decoded snapshot (welcome /
   * resume-ok snapshot mode, or a `whiteboard.restore` seed) and raise the
   * watermark to that snapshot's `seq`. Call this ALONGSIDE
   * `engine.applySnapshot(...)` (same `layers`) whenever the caller loads a
   * snapshot PNG — without it, a rewind computed later could fall back to a
   * blank baseline for bbox regions that were already baked before this
   * client joined (docs/_wip/undo-conflict-aware-2026-07-12.md「クライアント
   * 側の復元コスト」).
   *
   * `resetAll` (mirrors `applySnapshot`'s own `resetAll`, used by the
   * past-canvas「続きを描く」live-restore path): when `true`, the ENTIRE
   * `UndoBaselineStore` is discarded and rebuilt fresh (dropping every
   * tracked stroke + the previous baseline) before seeding — a restore
   * replaces the whole canvas, so any stroke tracked from the prior session
   * content is no longer meaningful. Omitted/`false` seeds onto the existing
   * store (welcome / resume — nothing is tracked yet at that point in the
   * connection lifecycle, so this is equivalent in practice, but doesn't
   * assume it).
   */
  seedUndoBaseline(
    layers: ReadonlyArray<{ id: number; rgba: Uint8ClampedArray }>,
    watermarkSeq: number,
    resetAll = false,
  ): void {
    this.guardLive();
    if (resetAll) {
      this.undoStore = this.makeUndoBaselineStore(this.canvas.width, this.canvas.height);
      this.ownStrokeSids.clear();
      this.ownLiveStrokes.clear();
      this.pendingRepairs = [];
      this.cancellableStack.length = 0;
      this.cancelledRegionSeqs.clear();
      this.notifyCancellableChange();
    }
    this.undoStore.seedBaseline(layers, watermarkSeq);
  }

  /**
   * "cancel 可能な自分の操作スタック" (C4) — MY strokes (server-confirmed ended) and MY
   * 範囲の移動・コピー (`whiteboard.region` echo 済み) that have not yet been cancelled,
   * 新しい順 (線は stroke.end の echo、移動は region の echo の到着順 = seq 順)
   * (LIFO 提示順 — a UI ordering suggestion only; the server is the sole
   * arbiter of actual cancel eligibility, see protocol-events.md §1.1).
   */
  getCancellableOps(): readonly CancellableOp[] {
    return [...this.cancellableStack].reverse();
  }

  /** Subscribe to `getCancellableOps()` changes. Not called on subscribe. */
  onCancellableOpsChange(callback: (stack: readonly CancellableOp[]) => void): () => void {
    this.cancellableListeners.add(callback);
    return () => {
      this.cancellableListeners.delete(callback);
    };
  }

  /**
   * Read-only bbox lookup for the conflict-aware Undo REJECTION UI (bbox
   * ハイライト, docs/_wip/undo-conflict-aware-2026-07-12.md「B' 実装での追加
   * 事項」). `stroke.cancel-rejected { reason: 'conflict' }` only carries
   * `{ sid, uid }` per conflicting stroke (protocol-events.md §1.1 — no bbox
   * on the wire, intentionally lightweight); the presentation layer calls
   * this to resolve each `conflicts[].sid` back to a canvas-space `Rect` for
   * an on-canvas highlight. Thin wrapper over `UndoBaselineStore.getStrokeBbox`
   * (the B' baseline already tracks every stroke's bbox for its own bbox-local
   * rewind, see there). `undefined` for an unknown sid, a not-yet-ended
   * stroke (in-flight growing bbox, f2, is not resolvable this way), or a
   * stroke that painted no pixels — callers must treat that as "no highlight
   * available for this sid", not an error (e.g. skip it and still render the
   * other conflicting strokes' highlights).
   */
  getUndoConflictBbox(sid: string): Rect | undefined {
    return this.undoStore.getStrokeBbox(sid);
  }

  /**
   * 範囲の移動・コピー (`regionSeq`) が絵を変える矩形 (元と置き先、全対象層の重複を除いたもの)。
   * 取り消しが `conflict` で拒否されたときに「取り消そうとした移動」を示すハイライトに使う。
   * 未追跡 (既に snapshot に焼かれた / 知らない seq) は空配列。
   */
  getUndoRegionRects(regionSeq: number): Rect[] {
    const out: Rect[] = [];
    for (const f of this.undoStore.getRegionFootprints(regionSeq) ?? []) {
      const dup = out.some(
        (r) => r.x0 === f.rect.x0 && r.y0 === f.rect.y0 && r.x1 === f.rect.x1 && r.y1 === f.rect.y1,
      );
      if (!dup) out.push(f.rect);
    }
    return out;
  }

  /**
   * Subscribe to `stroke.cancel-rejected` unicast results. The session layer
   * (which owns the `Transport` and therefore receives the unicast) calls
   * `notifyCancelRejected` on receipt; this engine holds no send-side API for
   * `stroke.cancel` itself (that lives in `useCases.requestCancelStroke`,
   * mirroring `sendClear` / `sendBackgroundSet` — the engine has no
   * `Transport` reference). UI presentation of the rejection (reason /
   * conflict highlight) is a later task; this is just the notification hook.
   */
  onCancelRejected(callback: (payload: UndoCancelRejection) => void): () => void {
    this.cancelRejectionListeners.add(callback);
    return () => {
      this.cancelRejectionListeners.delete(callback);
    };
  }

  /**
   * Feed a `stroke.cancel-rejected` / `whiteboard.region-cancel-rejected` unicast payload —
   * see `onCancelRejected`.
   */
  notifyCancelRejected(payload: UndoCancelRejection): void {
    for (const cb of this.cancelRejectionListeners) {
      try {
        cb(payload);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[CanvasDrawingEngine] cancel-rejection listener threw', err);
      }
    }
  }

  // ─── Phase 6.9 — name label API ──────────────────────────────────────────

  /**
   * Toggle whether the UI should display NameLabels. The engine maintains
   * the underlying state regardless; this flag only changes what subscribers
   * see in the `mode` field of `onNameLabelsChange` notifications. The UI
   * layer is expected to render labels iff `mode === true`.
   *
   * Idempotent: calling with the current value is a no-op (no listener fire).
   */
  setNameLabelMode(enabled: boolean): void {
    this.guardLive();
    if (this.nameLabelMode === enabled) return;
    this.nameLabelMode = enabled;
    this.notifyNameLabelListeners();
  }

  /** Current visibility flag set by the most recent `setNameLabelMode`. */
  getNameLabelMode(): boolean {
    return this.nameLabelMode;
  }

  /**
   * Snapshot of the per-uid labels (defensively copied — caller can mutate
   * the returned array without affecting engine state). Real-time tracking
   * (2026-05-06): every `stroke.segment` mirrors the latest `(x, y)` into
   * the per-uid slot, so the returned array reflects the live brush
   * position of each peer who is currently drawing (or last drew) — not
   * just stroke endpoints. Order is map-insertion order (= roughly: order
   * in which each uid first produced a label).
   */
  getNameLabels(): NameLabel[] {
    return Array.from(this.nameLabels.values());
  }

  /**
   * Total number of distinct uids with a committed label. Convenience for
   * tests that don't want to allocate a fresh array on every assertion.
   */
  getNameLabelCount(): number {
    return this.nameLabels.size;
  }

  /**
   * Drop every label (committed + pending) and notify subscribers. Called
   * automatically from `applyRemoteStroke` on `whiteboard.clear`; exposed
   * publicly so tests / future callers (e.g. room reset) can trigger the
   * same effect without faking a clear broadcast. No-op when already empty.
   */
  clearNameLabels(): void {
    this.guardLive();
    if (this.nameLabels.size === 0 && this.pendingStrokes.size === 0) return;
    this.nameLabels.clear();
    this.pendingStrokes.clear();
    this.notifyNameLabelListeners();
  }

  /**
   * Subscribe to label state changes. The callback fires synchronously on
   * every mutation (registration, finalization, clear, mode toggle). Returns
   * an unsubscribe function. Listeners are NOT called on subscribe — query
   * `getNameLabels` / `getNameLabelMode` directly for initial state.
   *
   * Each notification carries both `mode` and a fresh defensive snapshot of
   * `labels`, so a React component can `setState` directly without a follow-up
   * `getNameLabels` round-trip.
   */
  onNameLabelsChange(
    callback: (state: { mode: boolean; labels: ReadonlyArray<NameLabel> }) => void,
  ): () => void {
    this.nameLabelListeners.add(callback);
    return () => {
      this.nameLabelListeners.delete(callback);
    };
  }

  // ─── Phase 6.9 — zoom API ────────────────────────────────────────────────

  /**
   * Set the display zoom in `[ZOOM_FIT_MIN, ZOOM_MAX]`. Out-of-range values
   * are clamped. M1-a 分数ズーム: fractional (continuous) values are accepted
   * as-is (the legacy integer-only `Math.trunc` was removed — integer-step
   * snapping for the ± UI is the caller's policy, see `zoom.ts` の
   * `zoomStepIn`/`zoomStepOut`). The engine applies the zoom to the canvas
   * via `transform: scale()` + `transform-origin: 0 0` and sets
   * `image-rendering: pixelated` when zoom >= 1 (legacy nearest-neighbor
   * look) / `auto` when zoom < 1 (縮小 pixelated は見づらい、M1-a).
   *
   * Pointer-event coordinate inversion is the React layer's responsibility,
   * and goes through the `CanvasViewMapper` (`viewport.ts` §1b), which
   * already absorbs zoom — no extra bookkeeping needed on the consumer side.
   *
   * Idempotent: calling with the current zoom is a no-op (no listener fire,
   * no style mutation).
   */
  setZoom(zoom: number): void {
    this.guardLive();
    const next = clampZoom(zoom);
    if (next === this.zoom) return;
    this.zoom = next;
    this.applyZoomStyle();
    this.notifyDisplayScale();
    for (const cb of this.zoomListeners) {
      try {
        cb(next);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[CanvasDrawingEngine] zoom listener threw', err);
      }
    }
  }

  /** Current display zoom factor (連続値、`ZOOM_FIT_MIN`..`ZOOM_MAX`). */
  getZoom(): number {
    return this.zoom;
  }

  /**
   * Subscribe to zoom changes. Returns an unsubscribe function. Listener is
   * NOT called on subscribe.
   */
  onZoomChange(callback: (zoom: number) => void): () => void {
    this.zoomListeners.add(callback);
    return () => {
      this.zoomListeners.delete(callback);
    };
  }

  // ─── ドットバイドット表示 API ────────────────────────────────────────────

  /** 現在の表示倍率 (CSS px / canvas px)。CSS 幾何・ポインタ座標変換はこれを使う。 */
  getDisplayScale(): number {
    return displayScaleFor(this.zoom, this.dpr, this.dotByDotMode);
  }

  /** 任意の利用者 zoom に対する表示倍率 (現在の dpr / mode で換算)。 */
  displayScaleOf(zoom: number): number {
    return displayScaleFor(zoom, this.dpr, this.dotByDotMode);
  }

  /** zoom 1x の表示倍率の逆数 (有効時 dpr、無効時 1)。fit 計算の換算用。 */
  getDotByDotDivisor(): number {
    return dotByDotDivisor(this.dpr, this.dotByDotMode);
  }

  /** 現在の devicePixelRatio (engine が追従している値)。 */
  getDevicePixelRatio(): number {
    return this.dpr;
  }

  /** ドットバイドット表示の設定 (`'auto'` 既定 / `'off'` = 常に従来どおり)。 */
  setDotByDotMode(mode: DotByDotMode): void {
    this.guardLive();
    if (mode === this.dotByDotMode) return;
    this.dotByDotMode = mode;
    this.refreshDisplayScale();
  }

  getDotByDotMode(): DotByDotMode {
    return this.dotByDotMode;
  }

  /** 表示倍率が変わったとき (zoom / mode / dpr) に呼ばれる。解除関数を返す。 */
  onDisplayScaleChange(callback: (scale: number) => void): () => void {
    this.displayScaleListeners.add(callback);
    return () => {
      this.displayScaleListeners.delete(callback);
    };
  }

  /** dpr を読み直す (テスト・外部から呼べる)。変わっていれば表示を合わせ直す。 */
  syncDevicePixelRatio(dpr?: number): void {
    const next = dpr ?? (typeof window !== 'undefined' ? window.devicePixelRatio : 1);
    const v = Number.isFinite(next) && next > 0 ? next : 1;
    if (v === this.dpr) return;
    this.dpr = v;
    this.refreshDisplayScale();
  }

  private refreshDisplayScale(): void {
    this.applyZoomStyle();
    this.notifyDisplayScale();
  }

  private notifyDisplayScale(): void {
    const scale = this.getDisplayScale();
    for (const cb of this.displayScaleListeners) {
      try {
        cb(scale);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[CanvasDrawingEngine] display-scale listener threw', err);
      }
    }
  }

  /**
   * dpr の変化を `matchMedia('(resolution: Ndppx)')` の change で検知する定石。
   * 変化のたびに新しい dpr で張り直す。dispose で必ず解除。
   */
  private startWatchingDpr(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    this.syncDevicePixelRatio();
    const arm = (): void => {
      const mql = window.matchMedia(`(resolution: ${this.dpr}dppx)`);
      const onChange = (): void => {
        mql.removeEventListener('change', onChange);
        this.syncDevicePixelRatio();
        arm();
      };
      mql.addEventListener('change', onChange);
      this.dprUnwatch = () => mql.removeEventListener('change', onChange);
    };
    arm();
  }

  // ─── canvas-flip (view flip, 2026-06-07) — flipX API ─────────────────────

  /**
   * Toggle the horizontal **view flip** (= mirror the display left↔right).
   * Pure display state: pixel data and stroke coordinates are unchanged
   * (docs/_wip/canvas-flip.md §確定方針 1). The engine re-applies the canvas
   * `transform` (zoom + flip composed) and notifies subscribers.
   *
   * Idempotent: calling with the current value is a no-op (no listener fire,
   * no style mutation) — same contract as `setZoom`.
   */
  setFlipX(flip: boolean): void {
    this.guardLive();
    if (flip === this.flipX) return;
    this.flipX = flip;
    this.applyZoomStyle();
    for (const cb of this.flipListeners) {
      try {
        cb(flip);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[CanvasDrawingEngine] flip listener threw', err);
      }
    }
  }

  /** Current horizontal view-flip state (`true` = mirrored). */
  getFlipX(): boolean {
    return this.flipX;
  }

  /**
   * Subscribe to view-flip changes. Returns an unsubscribe function. Listener
   * is NOT called on subscribe — query `getFlipX` for initial state (zoom と
   * 同型).
   */
  onFlipXChange(callback: (flip: boolean) => void): () => void {
    this.flipListeners.add(callback);
    return () => {
      this.flipListeners.delete(callback);
    };
  }

  // ─── Phase 7 Task 7-8a — imagepaste preview / commit API ─────────────────

  /**
   * Begin (or replace) a local imagepaste preview. The bitmap is decoded
   * to RGBA bytes and pushed to the engine core as an overlay; UI buttons
   * subscribed via `onImagepastePreviewChange` are notified.
   *
   * Calling this while a preview already exists silently replaces the
   * previous one (callers normally cancel first; if they don't, the most
   * recent paste wins, mirroring how single-instance modal UI behaves).
   */
  startImagepastePreview(args: {
    bitmap: ImageBitmap;
    x: number;
    y: number;
    width: number;
    height: number;
    layerId?: number;
  }): void {
    this.guardLive();
    this.endLayerSolo(); // 強調と重ならない
    const decoded = this.imagepasteDecodeBitmapFn(args.bitmap);
    // The UI may want to display the bitmap at a different size than its
    // pixel dimensions (e.g. fit-to-canvas downscale before paste). For
    // now we paste at the bitmap's native size and trust the UI to have
    // chosen sensible (x, y). The width/height args are recorded so the
    // overlay state matches what the caller intends to broadcast later.
    void args.width;
    void args.height;
    const layerId = args.layerId ?? 0;
    this.imagepastePreview = {
      source: args.bitmap,
      rgba: decoded.rgba,
      rgbaWidth: decoded.width,
      rgbaHeight: decoded.height,
      width: decoded.width,
      height: decoded.height,
      x: args.x,
      y: args.y,
      layerId,
      locked: false,
    };
    this.imagepasteDrag = null;
    this.pushImagepasteOverlayStart();
    this.notifyImagepasteListeners();
  }

  /**
   * Move the preview to a new top-left position. No-op when no preview is
   * active, when the preview is locked, or when the new position equals
   * the current one.
   */
  setImagepastePreviewPosition(x: number, y: number): void {
    this.guardLive();
    const prev = this.imagepastePreview;
    if (prev === null || prev.locked) return;
    if (prev.x === x && prev.y === y) return;
    this.imagepastePreview = { ...prev, x, y };
    // Drag perf hot-fix (2026-05-31): only ship x/y over the worker
    // boundary; the engine reuses the rgba buffer it retained on start.
    this.pushImagepasteOverlayMove();
    this.notifyImagepasteListeners();
  }

  /**
   * Resize (and optionally reposition) the active preview — imagepaste リサイズ
   * (A案 = 送信前リサンプル). `(x, y)` is the new top-left in layer coords,
   * `width`/`height` the target size in px (rounded to an integer, min 1).
   *
   * - `ImagepastePreviewState` (x/y/width/height) updates **synchronously** and
   *   `onImagepastePreviewChange` fires, so hit-test / drag / UI handles follow
   *   the new size immediately.
   * - The overlay pixels are re-sampled from the ORIGINAL bitmap (never from a
   *   previous resample) but **coalesced to one resample per frame**: pointermove
   *   can fire faster than rAF, and each resample ships a new rgba buffer over
   *   the worker boundary (the drag-perf hot-fix of 2026-05-31 avoided exactly
   *   that cost for plain moves). The worker therefore shows the previous-size
   *   pixels for at most one frame. Position-only calls take the cheap
   *   `setImagepastePreviewPosition` path.
   * - No-op when no preview is active or it is locked.
   */
  setImagepastePreviewRect(x: number, y: number, width: number, height: number): void {
    this.guardLive();
    const prev = this.imagepastePreview;
    if (prev === null || prev.locked) return;
    const w = normalizeImagepasteSize(width);
    const h = normalizeImagepasteSize(height);
    if (w === prev.width && h === prev.height) {
      this.setImagepastePreviewPosition(x, y);
      return;
    }
    this.imagepastePreview = { ...prev, x, y, width: w, height: h };
    this.scheduleImagepasteResample();
    this.notifyImagepasteListeners();
  }

  /**
   * PNG `Blob` of the preview at its **current** `width x height`, for upload on
   * confirm. Produced by the same `resampleBitmapToRgba` function as the
   * overlay pixels (WYSIWYG: what the originator sees == what everyone gets).
   * Re-samples from the original bitmap (the overlay's rgba may have been
   * transferred to the worker). Resolves `null` when there is no preview or
   * PNG encoding is unavailable.
   */
  async getImagepastePreviewBlob(): Promise<Blob | null> {
    this.guardLive();
    const p = this.imagepastePreview;
    if (p === null) return null;
    const r = this.imagepasteResampleFn(p.source, p.width, p.height);
    return await this.imagepastePngBlobFn(r.rgba, r.width, r.height);
  }

  /** Coalesce resample work to one run per frame (rAF; microtask where rAF is absent). */
  private scheduleImagepasteResample(): void {
    if (this.imagepasteResampleScheduled) return;
    this.imagepasteResampleScheduled = true;
    const run = (): void => {
      this.imagepasteResampleScheduled = false;
      if (this.disposed) return;
      this.flushImagepasteResample();
    };
    if (typeof globalThis.requestAnimationFrame === 'function') {
      globalThis.requestAnimationFrame(run);
    } else {
      queueMicrotask(run);
    }
  }

  private flushImagepasteResample(): void {
    const p = this.imagepastePreview;
    // Locked previews still flush: a resize right before confirm must reach the overlay.
    if (p === null) return;
    if (p.rgbaWidth === p.width && p.rgbaHeight === p.height) return;
    const r = this.imagepasteResampleFn(p.source, p.width, p.height);
    this.imagepastePreview = { ...p, rgba: r.rgba, rgbaWidth: r.width, rgbaHeight: r.height };
    // `imagepaste.preview.start` replaces the overlay in the core (repaints the
    // union of old+new rects) and transfers the new buffer zero-copy.
    this.pushImagepasteOverlayStart();
  }

  /**
   * Move the active preview to a different **target layer** so its z
   * position matches where the paste will land (imagepaste×レイヤー実用化,
   * A案 WYSIWYG, 2026-06-14). Only the z changes — the bitmap / position
   * are unchanged. Called by the UI when the user switches the target
   * layer during the imagepaste drag (overlay-B レイヤー切替).
   *
   * No-op when no preview is active, when the preview is locked (= already
   * committing, layer is fixed), or when the layer is unchanged. The
   * engine-core re-composites the overlay's rect so it disappears from the
   * old z and reappears on top of the new layer; `Layer.main` is untouched.
   */
  setImagepastePreviewLayer(layerId: number): void {
    this.guardLive();
    const prev = this.imagepastePreview;
    if (prev === null || prev.locked) return;
    if (prev.layerId === layerId) return;
    this.imagepastePreview = { ...prev, layerId };
    this.pushImagepasteOverlaySetLayer(layerId);
    // Listeners observe x/y/width/height/locked only (not layerId), so the
    // z change doesn't alter the public preview state. We still notify for
    // forward-compat / parity with the other mutators.
    this.notifyImagepasteListeners();
  }

  /**
   * Lock the preview so drag is disabled (the caller is now uploading +
   * waiting for the broadcast). Listeners are notified so UI can disable
   * the move handle / change cursor if it wants.
   */
  lockImagepastePreview(): void {
    this.guardLive();
    const prev = this.imagepastePreview;
    if (prev === null || prev.locked) return;
    this.imagepastePreview = { ...prev, locked: true };
    this.imagepasteDrag = null;
    this.notifyImagepasteListeners();
  }

  /**
   * Cancel the preview (user clicked Cancel, upload failed, or the
   * caller is replacing it). The overlay is removed and listeners are
   * notified. No-op when no preview is active.
   */
  cancelImagepastePreview(): void {
    this.guardLive();
    if (this.imagepastePreview === null) return;
    this.imagepastePreview = null;
    this.imagepasteDrag = null;
    this.pushImagepasteOverlayEnd();
    this.notifyImagepasteListeners();
  }

  /**
   * Apply a `whiteboard.imagepaste` broadcast directly. Public so tests
   * can drive it without going through `applyRemoteStroke`. The promise
   * resolves once the fetch + decode + commit have landed (or been
   * dropped on error).
   */
  async applyImagepaste(event: {
    url: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }): Promise<void> {
    this.guardLive();
    await this.fetchAndApplyImagepaste(event);
  }

  /** Current preview state (or null). */
  getImagepastePreviewState(): ImagepastePreviewState | null {
    if (this.imagepastePreview === null) return null;
    const p = this.imagepastePreview;
    return { x: p.x, y: p.y, width: p.width, height: p.height, locked: p.locked };
  }

  /** Convenience: true while any preview is active (locked or not). */
  isImagepastePreviewActive(): boolean {
    return this.imagepastePreview !== null;
  }

  /**
   * Subscribe to preview state changes. Returns an unsubscribe function.
   * The callback is invoked synchronously on every change (start / move /
   * lock / cancel / commit-clear) with the latest state — or `null` when
   * the preview has been cleared.
   *
   * Listeners are NOT called on subscription; the caller can read the
   * current state via `getImagepastePreviewState` if it needs initial
   * sync.
   */
  onImagepastePreviewChange(callback: (state: ImagepastePreviewState | null) => void): () => void {
    this.imagepasteListeners.add(callback);
    return () => {
      this.imagepasteListeners.delete(callback);
    };
  }

  /**
   * Hit-test: is the layer-space point `(x, y)` inside the current
   * preview rectangle? Returns false when no preview is active or the
   * point is outside the rectangle. Locked previews still hit-test
   * positive (so the React layer can choose to absorb the click and not
   * start a stroke); only `tryBeginImagepasteDrag` actually checks the
   * lock.
   */
  hitTestImagepastePreview(x: number, y: number): boolean {
    const p = this.imagepastePreview;
    if (p === null) return false;
    return x >= p.x && y >= p.y && x < p.x + p.width && y < p.y + p.height;
  }

  /**
   * Try to start a drag. Returns true if `(x, y)` lands inside an
   * unlocked preview; the React layer should then route subsequent
   * `pointermove`s to `updateImagepasteDrag` and the `pointerup` to
   * `endImagepasteDrag`. Returns false when no preview is active, the
   * preview is locked, or `(x, y)` is outside the preview rect — the
   * React layer interprets that as "preview is in front but click missed
   * it; do nothing" (architecture.md §19, Phase 7 Task 7-8a:
   * preview-active 中は通常の pen 操作を block する).
   */
  tryBeginImagepasteDrag(x: number, y: number): boolean {
    this.guardLive();
    const p = this.imagepastePreview;
    if (p === null) return false;
    if (p.locked) return false;
    if (!this.hitTestImagepastePreview(x, y)) return false;
    this.imagepasteDrag = { offsetX: x - p.x, offsetY: y - p.y };
    return true;
  }

  /** Update the preview position from a drag-in-progress pointermove. */
  updateImagepasteDrag(x: number, y: number): void {
    this.guardLive();
    if (this.imagepasteDrag === null) return;
    const drag = this.imagepasteDrag;
    this.setImagepastePreviewPosition(x - drag.offsetX, y - drag.offsetY);
  }

  /** Drag complete — preview stays at its new position; lock unchanged. */
  endImagepasteDrag(): void {
    this.guardLive();
    this.imagepasteDrag = null;
  }

  resize(width: number, height: number): void {
    this.guardLive();
    if (width <= 0 || height <= 0) return;
    const widthChanged = this.canvas.width !== width;
    if (widthChanged) this.canvas.width = width;
    if (this.canvas.height !== height) this.canvas.height = height;
    // canvas-flip (2026-06-07): the flip transform's translateX correction is
    // a function of the canvas (display-box) width, so a resize while flipped
    // would leave a stale translate. Re-apply the composed transform. (The
    // plain `scale(zoom)` path is width-independent, so this is flip-only.)
    if (widthChanged && this.flipX) this.applyZoomStyle();
    this.dropLayerSoloLocal(); // the core ends it on resize
    this.send({ t: 'resize', width, height });
    // conflict-aware Undo (C1) — `EngineCore.resize` wipes every layer to a
    // fresh transparent bitmap, so any tracked stroke / baked baseline
    // pixels from before the resize are moot. A fresh store also picks up
    // the new dims (bbox clamping references canvas size).
    this.undoStore = this.makeUndoBaselineStore(width, height);
    this.thumbJank?.stop();
    this.thumbJank = null;
    this.thumbListeners.clear();
    this.thumbStateListeners.clear();
    this.thumbCache.clear();
    this.thumbEnabled = false;
    this.ownStrokeSids.clear();
    this.ownLiveStrokes.clear();
    this.pendingRepairs = [];
    this.cancellableStack.length = 0;
    this.cancelledRegionSeqs.clear();
    this.ownRegionUid = null;
    this.notifyCancellableChange();
  }

  /**
   * Construct a fresh `UndoBaselineStore` sized to the current canvas,
   * wiring its C2 cap-eviction diagnostics (`ImagepasteBitmapEvictedInfo`)
   * to `console.warn` — `UndoBaselineStore` itself has no DOM/Node globals
   * available (its `tsconfig` `lib` set excludes `console`, see
   * `UndoBaselineStoreOptions.onImagepasteBitmapEvicted`'s doc), so this is
   * the single place that turns "a paste's decoded bitmap got evicted to
   * stay under the memory cap" into an actual log line. All three
   * `UndoBaselineStore` construction sites (initial boot, `resetAll` reseed,
   * `resize`) share this helper so the wiring can't drift between them.
   */
  private makeUndoBaselineStore(width: number, height: number): UndoBaselineStore {
    return new UndoBaselineStore(width, height, CLASSIC_LAYER_COUNT, undefined, {
      onImagepasteBitmapEvicted: (info: ImagepasteBitmapEvictedInfo) => {
        // eslint-disable-next-line no-console
        console.warn(
          '[CanvasDrawingEngine] UndoBaselineStore imagepaste bitmap cap exceeded — ' +
            `evicting seq=${info.seq} (${info.evictedBytes} bytes, cap=${info.capBytes} bytes). ` +
            'Treated as a permanently-failed fetch for Undo bake/rewind purposes (bounded, accepted divergence).',
        );
      },
    });
  }

  /**
   * Sample a single pixel's RGB from the committed main bitmap of the
   * given layer. Used by the Classic UI スポイト (eyedropper) feature on
   * right-click / Alt+click (Phase 6.7 後追い、consult 2026-05-06).
   *
   * **Async to support the worker path** (PR 2-β, 2026-05-17): worker
   * mode keeps layer state off the main thread, so the read is dispatched
   * via `pixel.request` / `pixel.result` postMessage round-trip. Inline
   * path resolves synchronously via `Promise.resolve` so callers see one
   * unified `Promise<...>` shape.
   *
   *   - Inline path: forwards directly to `EngineCore.getLayerPixelColor`,
   *     wrapped in `Promise.resolve`. Effectively zero-cost.
   *   - Worker path: posts `pixel.request` with a monotonic `requestId`
   *     and a Promise that resolves on the matching `pixel.result`.
   *     Round-trip latency is ~1 frame at worst on modern hardware — not
   *     human-perceptible for a spuit click.
   *
   * Returns `null` for out-of-bounds coordinates or unknown `layerId`.
   * Alpha is intentionally dropped (旧版 `new Color(col)` 互換、spuit は
   * RGB だけ更新、現在の描画 alpha は維持する).
   *
   * Coordinates are layer-space (= same coord system as stroke points):
   * the caller (`DrawingCanvas`) converts client → canvas coords with the
   * `CanvasViewMapper` (`viewport.ts` §1b) before passing in.
   */
  async getLayerPixelColor(
    layerId: number,
    x: number,
    y: number,
  ): Promise<{ r: number; g: number; b: number } | null> {
    // Back-compat shim (spuit-sample-modes-2026-06-22): the legacy spuit read
    // was "selected layer + bg" = mode C. Delegates to `samplePixel` and drops
    // α (legacy callers only ever used RGB). New callers should use
    // `samplePixel` with an explicit mode.
    const px = await this.samplePixel('layer-bg', layerId, x, y);
    if (px === null) return null;
    return { r: px.r, g: px.g, b: px.b };
  }

  /**
   * Sample a single pixel for the spuit (eyedropper) feature with a selectable
   * source (spuit-sample-modes-2026-06-22 §2). Mirror of
   * `EngineCore.samplePixel`, exposed across the worker boundary:
   *
   *   - `'display'`  (A, 既定) — 全可視レイヤー + bg (見たまま). RGB のみ (`a`=255).
   *   - `'layer'`    (B) — 選択レイヤーの生ピクセル, bg 無視, in-flight 含む.
   *     実 **α を返す** → handler が `a === 0` で消しゴムへ切替, α 1..254 は RGB のみ採用.
   *   - `'layer-bg'` (C) — 選択レイヤー + bg (= legacy `getLayerPixelColor`). RGB のみ.
   *
   * Async to support the worker path (same `pixel.request` / `pixel.result`
   * round-trip as the legacy read; `mode` is threaded through). Inline path
   * resolves synchronously via `Promise.resolve`. Returns `null` for OOB
   * coordinates / unknown `layerId` / disposed engine / worker death.
   *
   * Coordinates are layer-space (= stroke-point coords); the caller converts
   * client → canvas coords before passing in.
   */
  async samplePixel(
    mode: SpuitMode,
    layerId: number,
    x: number,
    y: number,
  ): Promise<{ r: number; g: number; b: number; a: number } | null> {
    if (this.disposed) return null;
    if (this.inlineCore !== null) {
      return this.inlineCore.samplePixel(mode, layerId, x, y);
    }
    if (this.worker === null) return null;
    const requestId = this.nextPixelRequestId++;
    const promise = new Promise<{ r: number; g: number; b: number; a: number } | null>(
      (resolve) => {
        this.pixelRequests.set(requestId, resolve);
      },
    );
    this.send({ t: 'pixel.request', requestId, layerId, x, y, mode });
    return promise;
  }

  // ─── 範囲の移動・コピー (docs/_wip/region-move-2026-10-15.md) ─────────────

  /**
   * 層の「素の絵」を矩形で写し取る (`layer.main` = straight-alpha RGBA)。背景・👁・表示濃度・
   * 強調・遅延 commit ペンの描きかけバッファの影響なし。矩形は層の範囲に切り捨てる
   * (戻りの `x/y/width/height` は切り捨て後)。層が無い / 完全に範囲外 / 破棄済み /
   * worker 死亡なら `null`。
   *
   * worker 経路では先にリモート操作の coalesce queue を流してから `region.read.request` を送る
   * (worker は FIFO なので、それまでに届いた確定済みの絵が含まれる)。戻りの `rgba` は
   * 新しいバッファ (transferable で返ってくる)。
   */
  async readLayerRegion(
    layerId: number,
    x: number,
    y: number,
    w: number,
    h: number,
  ): Promise<RegionReadResult | null> {
    if (this.disposed) return null;
    if (this.inlineCore !== null) return this.inlineCore.readLayerRegion(layerId, x, y, w, h);
    if (this.worker === null) return null;
    this.flushCoalesceQueue();
    const requestId = this.nextRegionReadId++;
    const promise = new Promise<RegionReadResult | null>((resolve) => {
      this.regionReadRequests.set(requestId, resolve);
    });
    this.send({ t: 'region.read.request', requestId, layerId, x, y, w, h });
    return promise;
  }

  /**
   * 自分の移動・コピーの「確定から echo 適用まで、自分の新しい線を止める」(設計メモ
   * 「描きかけの線との衝突」)。UI は [確定] を押した瞬間にこれを呼び、`startStroke` の前に
   * `isOwnStrokeInputHeld()` を見てペンを下ろさせない。描いている途中の線は止めず、
   * `waitForOwnStrokesToEnd()` で終わるのを待ってから命令を送る。
   *
   * 解除: (a) `opts.releaseOnRegionFromUid` を渡せば、その uid の `whiteboard.region` の適用が
   * 済んだ (または失敗した) ときに自動で解除、(b) `releaseOwnStrokeInput()` で手動、
   * (c) `OWN_STROKE_HOLD_MAX_MS` 経っても echo が戻らなければ自動 (rejected / 切断で永久に
   * ペンが止まらないための保険)。二重に呼ぶと保持を引き継ぐ。
   */
  holdOwnStrokeInput(opts?: { releaseOnRegionFromUid?: string }): void {
    if (this.disposed) return;
    const wasHeld = this.ownStrokeHold !== null;
    // 範囲の移動・コピーを送る側 = この uid の region echo を「自分の取り消せる操作」に積む。
    if (opts?.releaseOnRegionFromUid !== undefined) this.ownRegionUid = opts.releaseOnRegionFromUid;
    if (this.ownStrokeHold !== null) clearTimeout(this.ownStrokeHold.timer);
    this.ownStrokeHold = {
      uid: opts?.releaseOnRegionFromUid ?? null,
      timer: setTimeout(() => this.releaseOwnStrokeInput(), OWN_STROKE_HOLD_MAX_MS),
    };
    if (!wasHeld) this.notifyOwnStrokeHold(true);
  }

  /**
   * 確定で送る範囲の絵を渡す (`items[i].srcLayerId` ごと)。自分の `whiteboard.region` の echo は
   * これで即座に適用する。`holdOwnStrokeInput` の後に呼ぶ。保持の解除で捨てる。
   */
  setOwnRegionImages(
    images: ReadonlyArray<{
      srcLayerId: number;
      width: number;
      height: number;
      rgba: Uint8ClampedArray;
    }>,
  ): void {
    if (this.disposed) return;
    this.ownRegionImages = new Map(
      images.map((i) => [i.srcLayerId, { rgba: i.rgba, width: i.width, height: i.height }]),
    );
  }

  /** `holdOwnStrokeInput` を解除する (保持していなければ no-op)。 */
  releaseOwnStrokeInput(): void {
    this.ownRegionImages = null;
    const hold = this.ownStrokeHold;
    if (hold === null) return;
    clearTimeout(hold.timer);
    this.ownStrokeHold = null;
    this.notifyOwnStrokeHold(false);
  }

  /** 自分の新しい線を止めている最中か。UI は `startStroke` の前にこれを見る。 */
  isOwnStrokeInputHeld(): boolean {
    return this.ownStrokeHold !== null;
  }

  /** `isOwnStrokeInputHeld()` の変化を購読する (購読時には呼ばない)。 */
  onOwnStrokeInputHeldChange(callback: (held: boolean) => void): () => void {
    this.ownStrokeHoldListeners.add(callback);
    return () => {
      this.ownStrokeHoldListeners.delete(callback);
    };
  }

  /** 描いている途中の自分の線があるか (`startStroke` 済みで `endStroke` 前)。 */
  hasActiveOwnStroke(): boolean {
    return this.active.size > 0;
  }

  /** 描いている途中の自分の線の sid (`startStroke` 済みで `endStroke` 前)。 */
  getActiveOwnStrokeSids(): readonly string[] {
    return [...this.active];
  }

  /**
   * 自分の線のうち、まだ「取り消せる」にも「却下/取り消し済み」にもなっていないもの
   * (描いている最中 + 終えて stroke.end の echo を待っている間) か。echo / cancelled /
   * 再読み込み(reseed) / 破棄で false になる。「描き終えたら元に戻す」の待ちを捨てる判断に使う。
   */
  isOwnStrokePending(sid: string): boolean {
    return this.ownLiveStrokes.has(sid);
  }

  /** 描いている途中の自分の線が全部終わるまで待つ (無ければ即 resolve)。破棄時も resolve。 */
  waitForOwnStrokesToEnd(): Promise<void> {
    if (this.disposed || this.active.size === 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      this.ownStrokeIdleWaiters.push(resolve);
    });
  }

  private resolveOwnStrokeIdleWaiters(): void {
    if (this.active.size > 0 || this.ownStrokeIdleWaiters.length === 0) return;
    const waiters = this.ownStrokeIdleWaiters;
    this.ownStrokeIdleWaiters = [];
    for (const resolve of waiters) resolve();
  }

  private notifyOwnStrokeHold(held: boolean): void {
    for (const cb of this.ownStrokeHoldListeners) {
      try {
        cb(held);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[CanvasDrawingEngine] own-stroke-hold listener threw', err);
      }
    }
  }

  /** `whiteboard.region` を `uid` から適用し終えた (失敗含む) ときに、その uid 向けの保持を外す。 */
  private releaseOwnStrokeHoldFor(uid: string): void {
    if (this.ownStrokeHold !== null && this.ownStrokeHold.uid === uid) this.releaseOwnStrokeInput();
  }

  /**
   * `whiteboard.region` の適用: 全 item の画像を fetch → 純 JS で decode (canvas を通さない) →
   * 全部揃ったら (1 つでも欠けたら何も適用せず) worker/inline に `region.apply` を送る。
   * 適用は「消す → 置く」(drawing-core `applyRegionMove`)。そのあと矩形の seq 順の作り直し
   * (`pendingRepairs`) に src 側・dst 側の両矩形を積む。**後続のイベントは止めない** (ゲートなし)。
   * 自分の echo も同じ経路で適用する。
   */
  private async fetchAndApplyRegion(event: RegionBroadcast): Promise<void> {
    if (this.disposed) return;
    let images: RegionImage[] | null = null;
    // 自分の echo: 手元に写しがあれば fetch せずそれを使う (空白を作らない)。取り出しは同期で
    // (保持の解除で消える前に)。メタの取り込み (`ingestUndoBroadcast`) は route の直後なので 1 tick 譲る。
    const own =
      this.ownRegionUid !== null && event.uid === this.ownRegionUid ? this.ownRegionImages : null;
    const ownImages = own === null ? null : event.items.map((item) => own.get(item.srcLayerId));
    try {
      if (ownImages !== null && ownImages.every((img) => img !== undefined)) {
        await Promise.resolve();
        images = ownImages as RegionImage[];
      } else {
        images = await Promise.all(event.items.map((item) => this.fetchRegionImage(item.url)));
      }
    } catch (cause) {
      // eslint-disable-next-line no-console
      console.warn('[CanvasDrawingEngine] region image fetch/decode failed', cause);
      images = null;
    }
    if (this.disposed) return;
    if (
      images !== null &&
      !images.every((img) => img.width === event.src.w && img.height === event.src.h)
    ) {
      // eslint-disable-next-line no-console
      console.warn('[CanvasDrawingEngine] region image size mismatch; skipping region');
      images = null;
    }
    // store には成否にかかわらず結果を報告する (失敗なら bake を止めず、全体を飛ばす)。
    this.undoStore.onRegionImages(event.seq, images);
    if (this.undoStore.hasPendingBake()) this.scheduleBakeStep();
    // 画像を待っている間に取り消された移動は適用しない (取り消し側の作り直しで矩形は既に
    // 「その移動が無い絵」になっている。いま置くと取り消したはずの移動が復活する)。
    if (images === null || this.cancelledRegionSeqs.has(event.seq)) {
      this.releaseLocalMaskFor(event.uid);
      this.releaseOwnStrokeHoldFor(event.uid);
      return;
    }
    this.sendRemote({
      t: 'region.apply',
      spec: { mode: event.mode, src: event.src, dst: event.dst, items: event.items },
      images,
    });
    // 画像を置く前に自分の次の線 (直送) が worker に届かないよう、queue を流してから保持を外す。
    this.flushCoalesceQueue();
    // 元の範囲が本当に消えた後 (region.apply の後ろ) で隠しを外す = ちらつかない。
    this.releaseLocalMaskFor(event.uid);
    for (const fp of this.undoStore.getRegionFootprints(event.seq) ?? []) {
      this.pendingRepairs.push({
        layerId: fp.layerId,
        seq: event.seq,
        rect: fp.rect,
        expiresAt: Date.now() + IMAGEPASTE_REPAIR_HOLD_MS,
      });
    }
    this.tryPendingRepairs();
    this.releaseOwnStrokeHoldFor(event.uid);
  }

  private async fetchRegionImage(url: string): Promise<RegionImage> {
    const res = await this.imagepasteFetchFn(url);
    if (!res.ok) throw new Error(`region image fetch status ${res.status}`);
    const buf = await res.arrayBuffer();
    return this.regionPngDecodeFn(new Uint8Array(buf));
  }

  /**
   * W3 Classic レイヤー追加 (2026-05-26) — export **all** layers + bg composited
   * into a single PNG `Blob`, **ignoring** per-layer display visibility.
   *
   * Used by the camera capture (`useRoomChrome.handleCapture`, Classic/Modern
   * 共通). The display canvas is visibility-aware (= a layer hidden via the
   * local 👁 toggle is skipped at display time), so reading the display canvas
   * would drop hidden layers from the capture. This path goes through
   * `EngineCore.compositeAllForExport()`, which never reads `visibility`, so
   * the capture always contains every layer (docs/_wip/w3-classic-layers.md
   * §2.3). The frontend feature-detects this method and falls back to the
   * display canvas when it returns `null`.
   *
   * **Async to support the worker path**: worker mode keeps layer state off
   * the main thread, so the composite is fetched via a `compositeAll.request`
   * / `compositeAll.result` round-trip (same plumbing as `getLayerPixelColor`).
   * Inline mode composites synchronously and wraps the result in a resolved
   * Promise so callers see one unified shape.
   *
   * Returns `null` when:
   *   - the engine is disposed (caller should fall back to the display canvas);
   *   - neither an inline core nor a worker is available;
   *   - the worker reports no layers / not-ready (= `null` composite);
   *   - PNG encoding is unavailable (the injected `pngEncodeFn` returns `null`).
   *
   * 2026-08-13: dataURL → `Blob` (capture-download-blob-2026-08-13.md §4.1)。
   * 旧名 `exportAllLayersDataUrl`。
   */
  async exportAllLayersBlob(): Promise<Blob | null> {
    if (this.disposed) return null;
    if (this.inlineCore !== null) {
      const rgba = this.inlineCore.compositeAllForExport();
      const width = this.canvas.width;
      const height = this.canvas.height;
      if (rgba.length === 0 || width === 0 || height === 0) return null;
      return this.pngEncodeFn(rgba, width, height);
    }
    if (this.worker === null) return null;
    const requestId = this.nextCompositeAllRequestId++;
    const result = await new Promise<{
      rgba: Uint8ClampedArray;
      width: number;
      height: number;
    } | null>((resolve) => {
      this.compositeAllRequests.set(requestId, resolve);
      this.send({ t: 'compositeAll.request', requestId });
    });
    if (result === null) return null;
    return this.pngEncodeFn(result.rgba, result.width, result.height);
  }

  // ─── PR 2-β follow-up (2026-05-21) — perf measure bridge over worker ──
  //
  // The drawing-core measure gate (= `enableDrawingPerfMeasure()`) lives in
  // the worker's JS realm when worker mode is active. These helpers
  // postMessage the matching control / drain into the worker so the e2e
  // harness can keep using its in-page bridge (`window.__OEKAKI_PERF__`)
  // without caring whether the engine is inline or worker-backed.
  //
  // Inline path: forwards to the directly-imported drawing-core module
  // synchronously (no IPC), wrapped in `Promise.resolve` so the caller
  // sees a uniform async shape regardless of mode.
  //
  // Worker path: ack-based round trip for enable/disable/clear (= small
  // postMessage, fire-and-forget would race the next stroke under the
  // harness's `await` chain); buffer drain for collect (= 1 postMessage
  // per harness candidate, not per measure entry).

  /**
   * Flip the drawing-core perf-measure gate ON in the worker (= where the
   * hot path lives in worker mode). Inline mode is a no-op here because
   * the inline drawing-core import lives in the main JS realm — the
   * harness flips that side directly via the top-level
   * `enableDrawingPerfMeasure()` import in `main.tsx`.
   *
   * Worker mode posts `perf.enable` and awaits `perf.ack` so the very
   * next stroke is guaranteed to run under the new gate state. Messages
   * sent before the worker's `ready` event are buffered (= `send()` queues
   * them in `pendingMessages`).
   *
   * No-op when disposed (resolves immediately). Safe to call repeatedly.
   */
  async enablePerfMeasure(): Promise<void> {
    if (this.disposed) return;
    if (this.worker === null) return;
    return this.sendPerfRequest('perf.enable');
  }

  /** Counterpart to `enablePerfMeasure`. Same Promise shape. */
  async disablePerfMeasure(): Promise<void> {
    if (this.disposed) return;
    if (this.worker === null) return;
    return this.sendPerfRequest('perf.disable');
  }

  /**
   * Drain the worker-side measure buffer **and** clear its PerformanceTimeline
   * for the drawing-core measure names. The harness uses this between
   * candidate runs to start each cell from a clean state.
   *
   * No-op (= resolves immediately) in inline-only mode — the inline path
   * lives in the main realm so the existing `__OEKAKI_PERF__.clearDrawing
   * PerfMeasures()` call covers it.
   */
  async clearPerfMeasures(): Promise<void> {
    if (this.disposed) return;
    if (this.worker === null) return;
    return this.sendPerfRequest('perf.clear');
  }

  /**
   * Collect the worker-side measure buffer (= every `measureDrawingHotPath`
   * call that fired since the last collect / clear). Returns an array of
   * `{ name, startTime, duration }` entries in chronological order.
   *
   * Returns `[]` in inline-only mode — the harness reads the inline path's
   * measures directly via `performance.getEntriesByType('measure')` from
   * main thread, which is the inline drawing-core's realm.
   *
   * The `startTime` is in the **worker's** `performance.now()` timebase;
   * callers should treat it as opaque for ordering and not compare it
   * against main-thread `performance.now()`.
   */
  async collectPerfMeasures(): Promise<
    ReadonlyArray<{ readonly name: string; readonly startTime: number; readonly duration: number }>
  > {
    if (this.disposed) return [];
    if (this.worker === null) return [];
    const requestId = this.nextPerfRequestId++;
    const promise = new Promise<
      ReadonlyArray<{
        readonly name: string;
        readonly startTime: number;
        readonly duration: number;
      }>
    >((resolve) => {
      this.perfCollectRequests.set(requestId, resolve);
    });
    this.send({ t: 'perf.collect', requestId });
    return promise;
  }

  /**
   * 描き味ベンチ専用 (inputLatencyProbe.ts): 入力→表示の遅れ記録の ON/OFF。
   * main 側の入力時刻バッファと worker 側の記録を同時に切り替える (ON で空に戻す)。
   * inline 経路 (worker なし) では記録先が無いので main 側フラグだけ。
   */
  async setInputLatency(enabled: boolean): Promise<void> {
    if (this.disposed) return;
    setInputLatencyEnabled(enabled);
    if (this.worker === null) return;
    const requestId = this.nextPerfRequestId++;
    const promise = new Promise<void>((resolve) => {
      this.perfAckRequests.set(requestId, resolve);
    });
    this.send({ t: 'latency.set', requestId, enabled });
    return promise;
  }

  /** 描き味ベンチ専用: worker の遅れ記録を取り出して空にする (inline / dispose 後は空)。 */
  async collectInputLatency(): Promise<
    ReadonlyArray<{ readonly first: boolean; readonly delayMs: number; readonly inputAt: number }>
  > {
    if (this.disposed || this.worker === null) return [];
    const requestId = this.nextPerfRequestId++;
    const promise = new Promise<
      ReadonlyArray<{ readonly first: boolean; readonly delayMs: number; readonly inputAt: number }>
    >((resolve) => {
      this.latencyCollectRequests.set(requestId, resolve);
    });
    this.send({ t: 'latency.collect', requestId });
    return promise;
  }

  // ─── Layer thumbnails (layer-thumbnails-2026-10-13.md) ───────────────────
  //
  // The engine only delivers small images: `onLayerThumbnail` gets one
  // `{ layerId, width, height, rgba }` per finished layer (rgba is straight-α,
  // owned by the listener). Nothing here touches React or the DOM; whether and
  // when to paint is the UI's call (hold off while the user's pen is down).
  // Default OFF - the UI turns it on when it has a surface to show.

  /**
   * Turn the thumbnail pipeline on/off. ON: the worker (or inline core) builds
   * thumbnails of every layer (a full rebuild first), only while the user is not
   * drawing, in <= 1 ms slices. OFF: everything torn down (per-layer dirty
   * tracking aside). `visible` = whether a surface showing them exists now.
   */
  setLayerThumbnailsEnabled(enabled: boolean, opts: { visible?: boolean } = {}): void {
    if (this.disposed) return;
    // Auto stop is sticky for the tab: a user OFF -> ON does not revive it.
    if (this.thumbState === 'stopped') return;
    this.thumbEnabled = enabled;
    if (opts.visible !== undefined) this.thumbVisible = opts.visible;
    this.setThumbState(enabled ? 'on' : 'off');
    if (!enabled) {
      this.thumbCache.clear();
      this.thumbJank?.stop();
      this.thumbJank = null;
    }
    const msg: EngineWorkerInbound = { t: 'thumb.config', enabled, visible: this.thumbVisible };
    if (this.worker !== null) {
      this.send(msg);
    } else if (this.inlineCore !== null) {
      this.inlineCore.setLayerThumbnails(
        enabled
          ? {
              visible: this.thumbVisible,
              onThumbnail: (t) => this.emitLayerThumbnail(t),
              onStopped: () => this.setThumbState('stopped'),
            }
          : null,
      );
    }
    if (enabled && this.thumbJank === null) {
      this.thumbJank = new ThumbnailJankSampler({
        isDrawing: () => this.active.size > 0,
        report: (ratio) => this.send({ t: 'thumb.jank', ratio }),
      });
      this.thumbJank.start();
    }
  }

  isLayerThumbnailsEnabled(): boolean {
    return this.thumbEnabled;
  }

  /**
   * 'off' (user / not requested) | 'on' | 'stopped' (the engine gave up on a
   * very slow device: no more thumbnails this tab - show numbers only).
   */
  getLayerThumbnailState(): LayerThumbnailState {
    return this.thumbState;
  }

  /** Subscribe to state changes (current state is replayed). Returns unsubscribe. */
  onLayerThumbnailState(cb: (s: LayerThumbnailState) => void): () => void {
    this.thumbStateListeners.add(cb);
    cb(this.thumbState);
    return () => {
      this.thumbStateListeners.delete(cb);
    };
  }

  private setThumbState(next: LayerThumbnailState): void {
    if (this.thumbState === next) return;
    this.thumbState = next;
    if (next === 'stopped') {
      this.thumbEnabled = false;
      this.thumbCache.clear();
      this.thumbJank?.stop();
      this.thumbJank = null;
    }
    for (const cb of this.thumbStateListeners) {
      try {
        cb(next);
      } catch {
        // A listener error must not stop the engine.
      }
    }
  }

  /**
   * Is a surface showing thumbnails right now? `false`: nothing is built (dirty
   * tracking continues); `true`: the backlog is rebuilt in small slices.
   * Classic's layer list / Modern's right panel: always true. The phone sheet:
   * true only while open.
   */
  setLayerThumbnailsVisible(visible: boolean): void {
    if (this.disposed) return;
    this.thumbVisible = visible;
    if (!this.thumbEnabled) return;
    this.send({ t: 'thumb.visible', visible });
  }

  /**
   * "Hold off" while the view is being moved (the same `suspended` the preview
   * thumbnail uses, `previewOverlay.ts` `shouldRegenThumbnail`).
   */
  setLayerThumbnailsSuspended(suspended: boolean): void {
    if (this.disposed || !this.thumbEnabled) return;
    this.send({ t: 'thumb.suspend', suspended });
  }

  /**
   * Subscribe to finished thumbnails. The latest one per layer is replayed
   * immediately. Returns the unsubscribe function. Listener work should be
   * tiny (paste into a small canvas); skip/delay while drawing.
   */
  onLayerThumbnail(cb: (t: LayerThumbnailMessage) => void): () => void {
    this.thumbListeners.add(cb);
    for (const t of this.thumbCache.values()) cb(t);
    return () => {
      this.thumbListeners.delete(cb);
    };
  }

  private emitLayerThumbnail(t: LayerThumbnailMessage): void {
    if (!this.thumbEnabled) return;
    this.thumbCache.set(t.layerId, t);
    for (const cb of this.thumbListeners) {
      try {
        cb(t);
      } catch {
        // A listener error must not stop the engine.
      }
    }
  }

  /** Bench (DEV hook): pin the back-off tier (`null` = automatic). */
  setLayerThumbnailTier(tier: 'normal' | 'slow' | 'very-slow' | null): void {
    if (this.disposed || !this.thumbEnabled) return;
    this.send({ t: 'thumb.tier', tier });
  }

  /** Bench (DEV hook): record the duration of every thumbnail slice. */
  async setLayerThumbnailStats(enabled: boolean): Promise<void> {
    if (this.disposed || !this.thumbEnabled) return;
    if (this.worker === null) {
      this.send({ t: 'thumb.stats.set', requestId: 0, enabled });
      return;
    }
    const requestId = this.nextPerfRequestId++;
    const promise = new Promise<void>((resolve) => {
      this.perfAckRequests.set(requestId, resolve);
    });
    this.send({ t: 'thumb.stats.set', requestId, enabled });
    return promise;
  }

  /** Bench (DEV hook): per-slice durations (ms, worker clock) since the last call. */
  async collectLayerThumbnailStats(): Promise<readonly number[]> {
    if (this.disposed) return [];
    if (this.worker === null) {
      return this.inlineCore?.getThumbnailScheduler()?.takeStats() ?? [];
    }
    const requestId = this.nextPerfRequestId++;
    const promise = new Promise<readonly number[]>((resolve) => {
      this.thumbStatsRequests.set(requestId, resolve);
    });
    this.send({ t: 'thumb.stats.collect', requestId });
    return promise;
  }

  /** Ack-based perf control round-trip (= enable / disable / clear). */
  private async sendPerfRequest(t: 'perf.enable' | 'perf.disable' | 'perf.clear'): Promise<void> {
    const requestId = this.nextPerfRequestId++;
    const promise = new Promise<void>((resolve) => {
      this.perfAckRequests.set(requestId, resolve);
    });
    this.send({ t, requestId });
    return promise;
  }

  /**
   * Apply a snapshot (one RGBA bitmap per layer) to the engine and
   * trigger a full repaint. Used by the Phase 5 snapshot path
   * (architecture.md §15) after the SnapshotLoader has fetched + decoded
   * `meta.json` + `layer-N.png` for `room.welcome.snapshot` /
   * `room.resume-ok.snapshot`.
   *
   * RGBA buffers MUST match `width * height * 4` for the layer they
   * target. Mismatched layers are silently skipped at the engine core
   * (caller's responsibility to `resize` first if dims differ).
   *
   * `opts.resetAll` (past-canvas「続きを描く」ライブ復元 hotfix, 2026-07-12):
   * clear EVERY layer to transparent + drop in-flight strokes BEFORE blitting
   * the seed layers, atomically inside the one `snapshot.apply` message. The
   * restore path sets this so it no longer needs a separate, delayed
   * `applyRemoteStroke([clear])` pixel wipe — which in worker mode raced the
   * immediate seed apply and blanked the canvas (the seed landed first, then
   * the queued clear wiped it). Everything now rides the single immediate
   * `send` channel, so ordering is guaranteed. Welcome / resume-ok omit it
   * (blank / fully-replaced engine — nothing stale to wipe).
   */
  applySnapshot(
    layers: ReadonlyArray<{ id: number; rgba: Uint8ClampedArray }>,
    opts?: { resetAll?: boolean },
  ): void {
    this.guardLive();
    const resetAll = opts?.resetAll ?? false;
    // With resetAll we must forward even an empty layer list — the whole
    // point is the all-layer wipe (a restore seed could legitimately be
    // empty layers over a differently-shaped current session). Without it,
    // an empty apply is a no-op (legacy behaviour).
    if (layers.length === 0 && !resetAll) return;
    this.send({ t: 'snapshot.apply', layers, ...(resetAll ? { resetAll: true } : {}) });
  }

  /**
   * Replace the canvas background. Used by the snapshot path + the
   * `whiteboard.background-set` broadcast so export / snapshot composite the
   * same bg the room agreed on (= what's carried in `meta.json.background`).
   *
   * **No display repaint** (2026-06-20, canvas-background-color memo §display):
   * the display compose no longer burns in the bg — the flat bg color is
   * emitted by the page's CSS background-color, which the frontend updates
   * directly from its own bg-color state. `EngineCore.setBackground` only
   * updates the property that export / snapshot / spuit read. This is what
   * makes the owner's interactive bg-color drag cheap (no per-frame full-canvas
   * re-composite). See architecture.md §19 layer/background separation
   * (2026-05-17) for the data-model split.
   */
  setBackground(next: { rgb: number; alpha: number }): void {
    this.guardLive();
    this.send({ t: 'background.set', background: next });
  }

  /**
   * Set a layer's **display** visibility (W3 Classic レイヤー追加, 2026-05-26).
   *
   * Called by the Classic floating layer popup's 👁 toggle. This is a
   * **client-local view** only:
   *   - NOT broadcast / NOT in the WS protocol (each user peeks at layers
   *     below independently; docs/_wip/w3-classic-layers.md §2.3).
   *   - hidden layers are skipped only in the display composite (the canvas
   *     the user sees + the camera capture that reads the display canvas).
   *   - the layer's pixels are untouched; unhiding shows the same content.
   *   - export-all / snapshot / spuit always include the layer regardless of
   *     this flag (see `EngineCore.compositeAllForExport` /
   *     `compositeForExport` / `getLayerPixelColor`).
   *
   * Forwards to the worker (or inline core); the core triggers a repaint
   * when the value actually changes. Out-of-range `layerId` is a no-op.
   */
  setLayerVisible(layerId: number, visible: boolean): void {
    this.guardLive();
    this.dropLayerSoloLocal(); // the core ends it (and repaints) on a visibility change
    this.send({ t: 'layer.visible', layerId, visible });
  }

  /**
   * Set a layer's **display** opacity (layer-display-opacity-2026-10-12.md).
   * Local only (not broadcast / not saved); export, snapshot, capture and the
   * spuit "display" mode all stay at 100%. Clamped to [0.1, 1]; NaN and
   * unknown layers are ignored. Cancels an active drag preview. Subscribers
   * (`onLayerOpacityChange`) are notified when the value changes.
   *
   * Usage (slider commit without drag): `engine.setLayerOpacity(2, 0.4)`.
   */
  setLayerOpacity(layerId: number, opacity: number): void {
    this.guardLive();
    const v = clampLayerOpacity(opacity);
    if (v === null || layerId < 0 || layerId >= this.layerOpacities.length) return;
    this.dropLayerSoloLocal();
    this.cancelOpacityPreviewLocal();
    if (this.layerOpacities[layerId] === v) {
      return;
    }
    this.layerOpacities[layerId] = v;
    this.send({ t: 'layer.opacity', layerId, opacity: v });
    this.notifyOpacity();
  }

  /** Display opacity of `layerId` — the live drag value while that layer is being previewed. */
  getLayerOpacity(layerId: number): number {
    const p = this.opacityPreview;
    if (p !== null && p.layerId === layerId) return p.value;
    return this.layerOpacities[layerId] ?? 1;
  }

  /** Every layer's display opacity (index = layerId), including the live drag value. */
  getLayerOpacities(): readonly number[] {
    return this.layerOpacities.map((_, i) => this.getLayerOpacity(i));
  }

  /** True when any layer is below 100% (drives the 「うすく表示中」札). */
  hasDimmedLayer(): boolean {
    return this.getLayerOpacities().some((v) => v < 1);
  }

  /** Back to 100% on every layer (the 札 tap). One repaint. */
  resetLayerOpacities(): void {
    this.guardLive();
    this.dropLayerSoloLocal();
    this.cancelOpacityPreviewLocal();
    let changed = false;
    for (let i = 0; i < this.layerOpacities.length; i++) {
      if (this.layerOpacities[i] !== 1) {
        this.layerOpacities[i] = 1;
        changed = true;
      }
    }
    if (!changed) return;
    this.send({ t: 'layer.opacity.reset' });
    this.notifyOpacity();
  }

  /**
   * Subscribe to opacity changes (set / reset / drag preview updates / preview
   * end). The callback gets the effective per-layer opacities (the live drag
   * value included). Returns an unsubscribe function.
   */
  onLayerOpacityChange(callback: (opacities: readonly number[]) => void): () => void {
    this.opacityListeners.add(callback);
    return () => {
      this.opacityListeners.delete(callback);
    };
  }

  /**
   * Start a smooth opacity drag for `layerId` (pointerdown on the slider
   * thumb). The engine builds the below / layer / above sheets once; follow
   * with `updateLayerOpacityPreview(v)` on pointermove and finish with
   * `endLayerOpacityPreview(commit)`. A preview already running (any layer) is
   * discarded first. While previewing, other drawing still updates layer data
   * but is not shown until the end (one repaint then).
   *
   * Usage:
   *   thumb.onPointerDown = () => engine.beginLayerOpacityPreview(id);
   *   thumb.onPointerMove = (e) => engine.updateLayerOpacityPreview(valueFrom(e));
   *   thumb.onPointerUp   = () => engine.endLayerOpacityPreview(true);   // cancel → false
   */
  beginLayerOpacityPreview(layerId: number): void {
    this.guardLive();
    if (layerId < 0 || layerId >= this.layerOpacities.length) return;
    if (this.layerSolo !== null) return; // 強調中は濃度プレビューを始めない
    if (this.opacityPreview !== null) this.endLayerOpacityPreview(false);
    this.opacityPreview = {
      layerId,
      value: this.layerOpacities[layerId] ?? 1,
      sentValue: null,
    };
    this.send({ t: 'layer.opacity.preview.begin', layerId });
  }

  /** Latest drag value (clamped). Sent to the core at most once per frame. */
  updateLayerOpacityPreview(opacity: number): void {
    this.guardLive();
    const p = this.opacityPreview;
    const v = clampLayerOpacity(opacity);
    if (p === null || v === null) return;
    p.value = v;
    this.notifyOpacity();
    this.scheduleOpacityPreviewFlush();
  }

  /** Finish the drag: `commit=true` keeps the dragged value, `false` restores the old one. */
  endLayerOpacityPreview(commit: boolean): void {
    this.guardLive();
    const p = this.opacityPreview;
    if (p === null) return;
    // Make sure the core has the final value before committing.
    this.flushOpacityPreview();
    this.opacityPreview = null;
    if (commit) this.layerOpacities[p.layerId] = p.value;
    this.send({ t: 'layer.opacity.preview.end', commit });
    this.notifyOpacity();
  }

  /**
   * 長押し強調: show `layerId` at 100% and dim every other layer to 20% (times
   * its own display opacity; 👁-hidden others stay hidden; background untouched).
   * Display only — nothing is saved, broadcast or changed in layer state. Call on
   * the long-press, end with `endLayerSolo()`. Returns false (nothing started)
   * while an opacity drag preview or imagepaste preview is active, or for an
   * unknown layer. Others' strokes arriving meanwhile are held back and shown by
   * the single repaint in `endLayerSolo`. Starting my own stroke (also a second
   * finger) ends it automatically — subscribe with `onLayerSoloChange` to drop
   * the 札.
   */
  beginLayerSolo(layerId: number): boolean {
    this.guardLive();
    if (layerId < 0 || layerId >= this.layerOpacities.length) return false;
    if (this.opacityPreview !== null || this.imagepastePreview !== null) return false;
    this.layerSolo = layerId;
    this.send({ t: 'layer.solo.begin', layerId });
    this.notifySolo();
    return true;
  }

  /** Leave 長押し強調 (one repaint). No-op when inactive. */
  endLayerSolo(): void {
    this.guardLive();
    if (this.layerSolo === null) return;
    this.layerSolo = null;
    this.send({ t: 'layer.solo.end' });
    this.notifySolo();
  }

  /** The soloed layer id, or null. */
  getLayerSolo(): number | null {
    return this.layerSolo;
  }

  /** Subscribe to solo start / end (incl. the automatic end by my own stroke). */
  onLayerSoloChange(callback: (layerId: number | null) => void): () => void {
    this.soloListeners.add(callback);
    return () => {
      this.soloListeners.delete(callback);
    };
  }

  /** Clear the mirrored state when the core ends solo by itself (no message needed). */
  /**
   * 範囲の移動で浮かせている間、元の範囲の絵を**手元の表示だけ**隠す (`null` で解除)。
   * `Layer.main` は変えない (送らない・snapshot / undo / サムネに影響しない)。表示濃度・👁・
   * 長押し強調・描画中の合成と併用できる。
   */
  setLocalRegionMask(
    mask: {
      layerIds: readonly number[];
      rect: { x: number; y: number; w: number; h: number };
    } | null,
    opts?: { releaseOnRegionFromUid?: string },
  ): void {
    this.guardLive();
    this.localMaskReleaseUid = mask === null ? null : (opts?.releaseOnRegionFromUid ?? null);
    this.send({ t: 'layer.localMask', mask });
  }

  /** 自分 (`uid`) の `whiteboard.region` を適用し終えた (失敗含む) とき、`region.apply` の後ろでマスクを外す。 */
  private releaseLocalMaskFor(uid: string): void {
    if (this.localMaskReleaseUid === null || this.localMaskReleaseUid !== uid) return;
    this.localMaskReleaseUid = null;
    this.send({ t: 'layer.localMask', mask: null });
  }

  private dropLayerSoloLocal(): void {
    if (this.layerSolo === null) return;
    this.layerSolo = null;
    this.notifySolo();
  }

  private notifySolo(): void {
    for (const cb of this.soloListeners) cb(this.layerSolo);
  }

  /** Drop the local preview state without sending `end` (the core's own setters cancel it). */
  private cancelOpacityPreviewLocal(): void {
    this.opacityPreview = null;
  }

  private flushOpacityPreview(): void {
    const p = this.opacityPreview;
    if (p === null || p.sentValue === p.value) return;
    p.sentValue = p.value;
    this.send({ t: 'layer.opacity.preview.update', opacity: p.value });
  }

  private scheduleOpacityPreviewFlush(): void {
    if (this.opacityPreviewFlushScheduled) return;
    this.opacityPreviewFlushScheduled = true;
    const run = (): void => {
      this.opacityPreviewFlushScheduled = false;
      if (this.disposed) return;
      this.flushOpacityPreview();
    };
    if (typeof globalThis.requestAnimationFrame === 'function') {
      globalThis.requestAnimationFrame(run);
    } else {
      queueMicrotask(run);
    }
  }

  private notifyOpacity(): void {
    if (this.opacityListeners.size === 0) return;
    const snapshot = this.getLayerOpacities();
    for (const cb of this.opacityListeners) cb(snapshot);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.releaseOwnStrokeInput();
    this.ownStrokeHoldListeners.clear();
    this.pendingRepairs = [];
    this.ownLiveStrokes.clear();
    this.active.clear();
    this.imagepastePreview = null;
    this.imagepasteDrag = null;
    this.imagepasteListeners.clear();
    this.opacityListeners.clear();
    this.soloListeners.clear();
    this.layerSolo = null;
    this.opacityPreview = null;
    this.filteredStrokeSids.clear();
    // conflict-aware Undo (C1-C4) — release subscribers + tracked state.
    // `bakeScheduled` / `undoRewindFlushScheduled` are checked by their
    // respective callbacks (`this.disposed` guard in the bake step; the
    // rewind flush's `send` is a no-op post-dispose via `guardLive`-adjacent
    // checks in `send`/`forwardToWorker`), so no explicit timer cancel is
    // needed here (unlike rAF/timeout, `queueMicrotask` / idle callbacks
    // have no cancel handle).
    this.ownStrokeSids.clear();
    this.ownLiveStrokes.clear();
    this.pendingRepairs = [];
    this.cancellableStack.length = 0;
    this.cancellableListeners.clear();
    this.cancelRejectionListeners.clear();
    // Phase 6.9 — release label / zoom subscribers + state.
    this.nameLabels.clear();
    this.pendingStrokes.clear();
    this.nameLabelListeners.clear();
    this.zoomListeners.clear();
    this.displayScaleListeners.clear();
    this.dprUnwatch?.();
    this.dprUnwatch = null;
    // canvas-flip (2026-06-07): release flip subscribers (zoom と同型).
    this.flipListeners.clear();
    // PR 2-β: release pending spuit promises + cancel backpressure rAF
    // so disposed-engine awaits don't hang and stray timers don't fire.
    this.drainPendingPixelRequests();
    // W3 Classic レイヤー追加 (2026-05-26): release pending camera-capture
    // (`exportAllLayersBlob`) promises so they fall back to null cleanly.
    this.drainPendingCompositeAllRequests();
    // PR 2-β follow-up (2026-05-21): release pending perf-bridge promises
    // so harness `await` chains complete cleanly when an engine is torn
    // down between candidate runs.
    this.drainPendingPerfRequests();
    // 2026-05-23 coalesce: tear down BOTH stages of the flush tick (rAF +
    // timeout fallback) and drop any pending remote ops so neither stray
    // timer leaks / fires after dispose and the queue doesn't pin memory.
    this.cancelFlushRaf();
    this.clearFlushTimeout();
    this.remoteCoalesce.clear();
    if (this.idleTimer !== null) {
      clearInterval(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.expireTimer !== null) {
      clearInterval(this.expireTimer);
      this.expireTimer = null;
    }
    if (this.worker !== null) {
      try {
        this.worker.postMessage({ t: 'dispose' } satisfies EngineWorkerInbound);
      } catch {
        // worker may already be torn down
      }
      this.worker.terminate();
      this.worker = null;
      this.workerReady = false;
      this.pendingMessages = [];
    }
    this.inlineCore?.detachBackend();
    this.inlineCore = null;

    if (import.meta.env.DEV) {
      unregisterEngineForDev(this);
    }
  }

  // ─── Phase 7 Task 7-8a — imagepaste internals ────────────────────────────

  /**
   * Drag perf hot-fix (2026-05-31, alpha-feedback-2026-05-31.md §5):
   * the overlay protocol is split into start / move / end so the rgba
   * buffer crosses the worker boundary exactly once (transferable,
   * zero-copy), and drag pointermoves only ship two numbers. The old
   * `pushImagepasteOverlay()` re-sent the full rgba on every move —
   * ~480 MB/s of structuredClone for a 1920×1080 paste at 60 Hz.
   *
   * Pushes a **start** message — used on `startImagepastePreview`. Hands
   * `rgba.buffer` to the worker as a Transferable so the sender view
   * detaches; the worker retains the buffer until `endImagepasteOverlay`
   * (or the next start, or a commit).
   */
  private pushImagepasteOverlayStart(): void {
    const p = this.imagepastePreview;
    if (p === null) return;
    this.send(
      {
        t: 'imagepaste.preview.start',
        overlay: {
          rgba: p.rgba,
          width: p.width,
          height: p.height,
          x: p.x,
          y: p.y,
          layerId: p.layerId,
        },
      },
      // Transfer the rgba buffer to the worker — main-thread view detaches.
      // Inline mode ignores `transfer` (no postMessage in flight), so tests
      // that share the same Uint8ClampedArray keep working.
      [p.rgba.buffer],
    );
  }

  /** Push a **move** — just the new (x, y). Hot-path on drag. */
  private pushImagepasteOverlayMove(): void {
    const p = this.imagepastePreview;
    if (p === null) return;
    this.send({ t: 'imagepaste.preview.move', x: p.x, y: p.y });
  }

  /**
   * Push a **setLayer** — re-target the overlay to a new layer z (imagepaste×
   * レイヤー実用化, 2026-06-14). Just one number; the engine reuses the rgba
   * buffer it retained on start and re-composites the overlay's rect at the
   * new z. No-op on the engine side when no preview is active.
   */
  private pushImagepasteOverlaySetLayer(layerId: number): void {
    this.send({ t: 'imagepaste.preview.setLayer', layerId });
  }

  /** Push an **end** — clear the overlay on the engine side. */
  private pushImagepasteOverlayEnd(): void {
    this.send({ t: 'imagepaste.preview.end' });
  }

  /** Notify all subscribers with a fresh snapshot of preview state. */
  private notifyImagepasteListeners(): void {
    const snapshot = this.getImagepastePreviewState();
    for (const cb of this.imagepasteListeners) {
      try {
        cb(snapshot);
      } catch (err) {
        // Listener errors must NOT take the engine down. Log + continue.
        // eslint-disable-next-line no-console
        console.warn('[CanvasDrawingEngine] imagepaste listener threw', err);
      }
    }
  }

  /**
   * Fetch the broadcast URL → decode → commit. On any failure (network,
   * 404, decode), drops the event with `console.warn` and clears any
   * preview overlay (broadcast is the commit signal; a failed commit
   * means we should stop showing the preview).
   *
   * `seq` (optional — omitted by the `applyImagepaste` public test helper,
   * which has no undo-store integration) feeds `UndoBaselineStore` the SAME
   * decoded bitmap this method fetches for display, via `onImagepasteBitmap`
   * (2026-07-23「第二弾設計確定」imagepaste × Undo 穴埋め — see that method's
   * doc for why success AND explicit failure both need to be reported, not
   * just success).
   */
  private async fetchAndApplyImagepaste(event: {
    url: string;
    x: number;
    y: number;
    width: number;
    height: number;
    layerId?: number;
    seq?: number;
  }): Promise<void> {
    if (this.disposed) return;
    const layerId = event.layerId ?? 0;
    let res: Response;
    try {
      res = await this.imagepasteFetchFn(event.url);
    } catch (cause) {
      // eslint-disable-next-line no-console
      console.warn('[CanvasDrawingEngine] imagepaste fetch failed', cause);
      this.dropImagepastePreviewOnError();
      this.markImagepasteBitmapFailed(event.seq);
      return;
    }
    if (this.disposed) return;
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.warn('[CanvasDrawingEngine] imagepaste fetch non-ok', res.status);
      this.dropImagepastePreviewOnError();
      this.markImagepasteBitmapFailed(event.seq);
      return;
    }
    let blob: Blob;
    try {
      blob = await res.blob();
    } catch (cause) {
      // eslint-disable-next-line no-console
      console.warn('[CanvasDrawingEngine] imagepaste blob read failed', cause);
      this.dropImagepastePreviewOnError();
      this.markImagepasteBitmapFailed(event.seq);
      return;
    }
    if (this.disposed) return;
    let bitmap: ImageBitmap;
    try {
      bitmap = await this.imagepasteCreateBitmapFn(blob);
    } catch (cause) {
      // eslint-disable-next-line no-console
      console.warn('[CanvasDrawingEngine] imagepaste createImageBitmap failed', cause);
      this.dropImagepastePreviewOnError();
      this.markImagepasteBitmapFailed(event.seq);
      return;
    }
    if (this.disposed) {
      try {
        bitmap.close?.();
      } catch {
        // ignore
      }
      return;
    }
    let decoded: { rgba: Uint8ClampedArray; width: number; height: number };
    try {
      decoded = this.imagepasteDecodeBitmapFn(bitmap);
    } catch (cause) {
      // eslint-disable-next-line no-console
      console.warn('[CanvasDrawingEngine] imagepaste decode failed', cause);
      this.dropImagepastePreviewOnError();
      this.markImagepasteBitmapFailed(event.seq);
      return;
    } finally {
      try {
        bitmap.close?.();
      } catch {
        // ignore
      }
    }
    if (this.disposed) return;
    if (event.seq !== undefined) {
      this.undoStore.onImagepasteBitmap(event.seq, decoded);
      // 再レビュー指摘 2 (2026-07-24): a `bakeStep` that stalled on this
      // paste while it was still 'pending' (C1) has already returned false
      // and parked the pump — `snapshot.published` (the only other
      // `scheduleBakeStep` trigger) may never arrive again in a quiet room,
      // leaving the baseline stalled and the clipped bitmap retained
      // indefinitely. Settling is exactly the unblock event, so re-pump.
      if (this.undoStore.hasPendingBake()) this.scheduleBakeStep();
    }
    // Drop the local preview (broadcast supersedes it — the broadcast
    // coordinates are authoritative).
    if (this.imagepastePreview !== null) {
      this.imagepastePreview = null;
      this.imagepasteDrag = null;
      this.notifyImagepasteListeners();
    }
    // Route through the coalesce queue as a barrier so the commit lands in
    // order relative to any pending remote stroke ops at the moment the async
    // fetch+decode resolves (imagepaste is a barrier — backpressure-coalesce.md
    // §順序保証). The ~1-frame flush latency is imperceptible for an image paste.
    this.sendRemote({
      t: 'imagepaste.commit',
      layerId,
      rgba: decoded.rgba,
      width: decoded.width,
      height: decoded.height,
      x: event.x,
      y: event.y,
    });
    // 画像は上の直書きでまず見せる (取得が済んだ時点で即表示)。そのうえで、取得を待つ
    // 間に先に手元へ描かれた後続の線で画像が上書きされた場合のために、矩形を seq 順
    // (server の snapshot と同じ) に作り直す。後続のイベントは待たせない。
    if (event.seq !== undefined) {
      this.pendingRepairs.push({
        layerId,
        seq: event.seq,
        rect: {
          x0: Math.floor(event.x),
          y0: Math.floor(event.y),
          x1: Math.floor(event.x) + decoded.width - 1,
          y1: Math.floor(event.y) + decoded.height - 1,
        },
        expiresAt: Date.now() + IMAGEPASTE_REPAIR_HOLD_MS,
      });
      this.tryPendingRepairs();
    }
  }

  private trackOwnLiveStart(sid: string, layerId: number, width: number): void {
    if (this.ownLiveStrokes.size >= OWN_LIVE_STROKE_CAP) {
      const oldest = this.ownLiveStrokes.keys().next();
      if (!oldest.done) this.ownLiveStrokes.delete(oldest.value);
    }
    this.ownLiveStrokes.set(sid, {
      layerId,
      margin: width / 2 + 3, // 半径 + AA/丸めの余白
      x0: Number.POSITIVE_INFINITY,
      y0: Number.POSITIVE_INFINITY,
      x1: Number.NEGATIVE_INFINITY,
      y1: Number.NEGATIVE_INFINITY,
      since: Date.now(),
    });
  }

  private trackOwnLivePoints(sid: string, points: ReadonlyArray<StrokePoint>): void {
    const e = this.ownLiveStrokes.get(sid);
    if (e === undefined) return;
    const m = e.margin;
    for (const p of points) {
      if (p.x - m < e.x0) e.x0 = p.x - m;
      if (p.y - m < e.y0) e.y0 = p.y - m;
      if (p.x + m > e.x1) e.x1 = p.x + m;
      if (p.y + m > e.y1) e.y1 = p.y + m;
    }
  }

  /**
   * 溜まっている作り直しを、できるものから実行する。次のものは保留して再試行
   * (自分の線の end echo / cancel が戻ったとき、または貼り付けから
   * `IMAGEPASTE_REPAIR_HOLD_MS` 経つまで):
   *   - 矩形に echo 前の自分の線が触れている (store に無いので、作り直すと消してしまう)。
   * 次のものは作り直さず、画像の直書き (取得できた時点の見た目) のままにする:
   *   - store がその貼り付けを作り直せない (baseline に焼かれた/失敗/画面外)。
   *   - 発言を隠す設定 (filterUids) がある (store は隠した人の線も持っているので、
   *     作り直すと隠したはずの線が現れる)。
   */
  private tryPendingRepairs(): void {
    if (this.disposed || this.pendingRepairs.length === 0) return;
    const now = Date.now();
    const keep: typeof this.pendingRepairs = [];
    for (const r of this.pendingRepairs) {
      if (now >= r.expiresAt) continue;
      if (
        this.filterUids.size > 0 ||
        !(this.undoStore.canRepairImagepaste(r.seq) || this.undoStore.canRepairRegion(r.seq))
      ) {
        continue;
      }
      let blocked = false;
      for (const [sid, e] of this.ownLiveStrokes) {
        // 戻らないまま残った線 (却下など) でいつまでも塞がないよう古いものは無視。
        if (now - e.since > IMAGEPASTE_REPAIR_HOLD_MS) {
          this.ownLiveStrokes.delete(sid);
          continue;
        }
        if (e.layerId !== r.layerId) continue;
        if (e.x1 < r.rect.x0 || e.x0 > r.rect.x1 || e.y1 < r.rect.y0 || e.y0 > r.rect.y1) continue;
        blocked = true;
        break;
      }
      if (blocked) {
        keep.push(r);
        continue;
      }
      // 溜まっているリモート操作を先に worker へ流してから、パッチで矩形を上書きする
      // (store は受信時点で全部取り込み済みなので、パッチはそれらを含む)。
      this.flushCoalesceQueue();
      const patch = this.undoStore.repairRegion(r.layerId, r.rect);
      if (patch !== null) this.sendRegionPatch(patch);
    }
    this.pendingRepairs = keep;
  }

  private dropImagepastePreviewOnError(): void {
    if (this.imagepastePreview === null) return;
    this.imagepastePreview = null;
    this.imagepasteDrag = null;
    this.pushImagepasteOverlayEnd();
    this.notifyImagepasteListeners();
  }

  /**
   * Report a permanent `whiteboard.imagepaste` fetch/decode failure to
   * `UndoBaselineStore` (2026-07-23「第二弾設計確定」) — a no-op if `seq` is
   * absent (the public `applyImagepaste` test helper has no undo-store
   * integration). See `UndoBaselineStore.onImagepasteBitmap`'s doc: passing
   * `null` here means bake/rewind will skip this paste forever, matching
   * what the live canvas ALSO does on a failed fetch (nothing painted).
   */
  private markImagepasteBitmapFailed(seq: number | undefined): void {
    if (seq === undefined) return;
    this.undoStore.onImagepasteBitmap(seq, null);
    // 再レビュー指摘 2 (2026-07-24): a confirmed failure unblocks a C1
    // bake stall the same way a success does (the paste is now settled
    // either way) — re-pump, see the success path's comment in
    // `fetchAndApplyImagepaste`.
    if (this.undoStore.hasPendingBake()) this.scheduleBakeStep();
  }

  // ─── Phase 6.9 — name label internals ────────────────────────────────────

  /**
   * Open a pending stroke entry on `stroke.start`. The protocol carries no
   * start-point coordinates (the first segment does), so `lastX/Y` start
   * `null` and `nameLabels` is NOT touched here — any existing label for
   * the same uid stays in place until the first segment arrives, at which
   * point it jumps to the live brush position. A duplicate `stroke.start`
   * (replay / resume re-delivery) re-seeds the entry with fresh metadata
   * and resets `lastX/Y`; the next segment is the source of truth.
   */
  private openPendingStroke(sid: string, entry: { name: string; uid: string; seq: number }): void {
    this.pendingStrokes.set(sid, { ...entry, lastX: null, lastY: null });
  }

  /**
   * Update the running last (x, y) of an in-flight stroke AND mirror that
   * point into `nameLabels[uid]` so the label tracks the brush in real time.
   * Fires the change listener whenever the per-uid slot meaningfully
   * changes (new uid, moved coords, or different stroke metadata). No-op
   * when no pending entry matches `sid` (e.g. start was filtered upstream).
   */
  private advancePendingStroke(sid: string, x: number, y: number): void {
    const pending = this.pendingStrokes.get(sid);
    if (pending === undefined) return;
    pending.lastX = x;
    pending.lastY = y;
    const prev = this.nameLabels.get(pending.uid);
    if (
      prev !== undefined &&
      prev.x === x &&
      prev.y === y &&
      prev.name === pending.name &&
      prev.seq === pending.seq
    ) {
      // Identical mirror (e.g. exact replay of the same segment, or a new
      // segment whose tail point happens to coincide with the previous tail
      // — a held-still brush). Skip listener fire to keep notifications
      // meaningful, AND intentionally do NOT refresh `birthtime`: 旧版踏襲
      // で「ペン静止 = 同値 tail = birthtime 更新されない = 1 秒で消える」
      // を一貫させる (Phase 6.9 TTL 仕様 2026-05-06). Updating birthtime here
      // would keep idle brushes alive forever as long as PointerEvent samples
      // keep arriving from a stationary stylus.
      return;
    }
    const next: NameLabel = {
      x,
      y,
      name: pending.name,
      uid: pending.uid,
      seq: pending.seq,
      birthtime: Date.now(),
    };
    this.nameLabels.set(pending.uid, next);
    this.notifyNameLabelListeners();
  }

  /**
   * Drain the pending entry on `stroke.end`. Pure cleanup: `nameLabels` is
   * NOT touched — the most recent `advancePendingStroke` already mirrored
   * the final segment tail into the per-uid slot. A coordinate-less stroke
   * (start → end with no segment) likewise leaves any prior label intact,
   * matching the legacy 「描き終え位置」 semantics for paths that produced
   * no pixels. Idempotent when `sid` is unknown (duplicate stroke.end /
   * late delivery).
   */
  private closePendingStroke(sid: string): void {
    this.pendingStrokes.delete(sid);
  }

  /** Fan out a fresh defensive snapshot to every label subscriber. */
  private notifyNameLabelListeners(): void {
    if (this.nameLabelListeners.size === 0) return;
    const snapshot: { mode: boolean; labels: ReadonlyArray<NameLabel> } = {
      mode: this.nameLabelMode,
      labels: Array.from(this.nameLabels.values()),
    };
    for (const cb of this.nameLabelListeners) {
      try {
        cb(snapshot);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[CanvasDrawingEngine] name-label listener threw', err);
      }
    }
  }

  // ─── Phase 6.9 — zoom internals ──────────────────────────────────────────

  /**
   * Apply the current zoom factor to the canvas DOM element via inline
   * style. We use `transform: scale()` instead of width/height so the
   * underlying pixel buffer stays at native resolution (no GPU upload of
   * a giant ImageData every redraw — architecture.md §Phase 6.9).
   *
   * Defensive against the test environment: jsdom-less node tests use a
   * minimal canvas mock that may lack a `style` object. A missing style
   * object is silently tolerated; the engine still tracks zoom in JS state
   * and the listener fires regardless.
   */
  private applyZoomStyle(): void {
    type CanvasWithStyle = HTMLCanvasElement & {
      style?: { transform?: string; transformOrigin?: string; imageRendering?: string };
    };
    const cws = this.canvas as CanvasWithStyle;
    const style = cws.style;
    if (style === undefined) return;
    if (this.flipX) {
      // canvas-flip (view flip, 2026-06-07, docs/_wip/canvas-flip.md §1):
      // a negative scaleX with transform-origin '0 0' would push the canvas
      // to negative X (off the left edge of its box), so we translate it
      // back by one display-box width first. The display box width equals
      // `this.canvas.width`: the React layer sets `style.width = width` to
      // match the native buffer 1:1 (the *layout* box is never dpr-scaled; the
      // dot-by-dot factor lives only in `getDisplayScale()`), so CSS px == buffer px. Both axes scale by
      // `zoom`; only X is mirrored.
      const w = this.canvas.width;
      const s = this.getDisplayScale();
      style.transform = `translateX(${s * w}px) scale(${-s}, ${s})`;
    } else {
      const s = this.getDisplayScale();
      style.transform = s === 1 ? '' : `scale(${s})`;
    }
    style.transformOrigin = '0 0';
    // M1-a 分数ズーム: 拡大 (>= 1) は従来どおり nearest-neighbor (pixelated =
    // legacy の描き味)。縮小 (< 1) は pixelated だとドット抜けで見づらいので
    // ブラウザ既定の smoothing (auto) に切り替える (modern-phone-shell §3.1)。
    style.imageRendering = this.zoom >= 1 ? 'pixelated' : 'auto';
  }

  // ─── internals ───────────────────────────────────────────────────────────

  /**
   * Forward a message to the renderer. This is the **local / immediate** path:
   * everything sent here goes straight to the worker (or inline core) with no
   * gating. Local strokes (`addPoints`) and one-shot control messages
   * (resize / snapshot / pixel.request / perf.* / etc.) use this.
   *
   * Remote (peer) broadcasts do NOT call `send` directly — they go through
   * `sendRemote` → the coalesce queue → `forwardToWorker` on the next flush
   * tick. See `sendRemote` and `RemoteCoalesceQueue` for why.
   */
  private send(msg: EngineWorkerInbound, transfer?: ReadonlyArray<Transferable>): void {
    if (this.worker !== null) {
      this.forwardToWorker(msg, transfer);
      return;
    }
    if (this.inlineCore !== null) {
      // Inline path skips postMessage; `transfer` is meaningless here. The
      // caller MUST NOT rely on detachment when running inline — the same
      // view is shared with the core. Drag perf hot-fix (2026-05-31) ships
      // the rgba *by reference* on this path, which is fine because there
      // is no separate worker thread to lose the buffer to.
      applyMessageInline(this.inlineCore, msg);
    }
  }

  /**
   * Enqueue a **remote** (peer-broadcast) message into the coalesce queue and
   * make sure a flush tick is armed (docs/_wip/backpressure-coalesce.md).
   *
   * `stroke.points` are coalesced (adjacent same-sid runs merge into one
   * postMessage); every other message is a barrier (forwarded verbatim, order
   * preserved). The queue never drops — under a cap it decimates instead.
   *
   * Inline mode (no worker) has no postMessage cost, so we apply remote
   * messages immediately and skip the queue entirely.
   */
  private sendRemote(msg: EngineWorkerInbound): void {
    if (this.worker === null) {
      if (this.inlineCore !== null) applyMessageInline(this.inlineCore, msg);
      return;
    }
    if (msg.t === 'stroke.points') {
      this.remoteCoalesce.enqueuePoints(msg.sid, msg.points, msg.nowMs);
    } else {
      this.remoteCoalesce.enqueueBarrier(msg);
    }
    this.armCoalesceFlush();
  }

  /**
   * Raw worker post (or pending-queue buffer before `ready`).
   *
   * `transfer` (optional) enumerates buffers whose ownership transfers to
   * the worker (= zero-copy, sender view detaches). Used by
   * `imagepaste.preview.start` to ship the rgba buffer (= up to 8+ MB for
   * a 1920×1080 paste) without structuredClone (drag perf hot-fix,
   * 2026-05-31, alpha-feedback-2026-05-31.md §5). Pending-buffered
   * messages (before worker `ready`) preserve their transfer list so the
   * flush at `ready` keeps the same zero-copy semantics.
   */
  private forwardToWorker(msg: EngineWorkerInbound, transfer?: ReadonlyArray<Transferable>): void {
    if (this.worker === null) return;
    const tlist =
      transfer !== undefined && transfer.length > 0 ? (transfer as Transferable[]) : undefined;
    if (this.workerReady) {
      if (tlist !== undefined) {
        this.worker.postMessage(msg, tlist);
      } else {
        this.worker.postMessage(msg);
      }
    } else {
      this.pendingMessages.push(tlist !== undefined ? { msg, transfer: tlist } : { msg });
    }
  }

  /**
   * Arm a flush of the remote coalesce queue (if not already armed).
   *
   * 案B (rAF + timeout 二段, reused from the 2026-05-23 bg-tab fix): we arm
   * BOTH a `requestAnimationFrame` and a `setTimeout(COALESCE_FLUSH_FALLBACK_MS)`;
   * whichever fires first flushes and cancels the other. This is essential
   * because browsers PAUSE rAF in backgrounded tabs — without the timeout
   * fallback the queue would never flush in the background and a backgrounded
   * viewer would stop seeing peers' strokes (only worse now, since we no
   * longer drop). In the foreground rAF (~16 ms) always wins before the
   * timeout (250 ms), so the flush is frame-aligned (1 flush/frame); in the
   * background the throttled timeout (~1 s) flushes the accumulated, coalesced
   * points in batches → no loss, curve fully reconstructed on resume.
   *
   * Falls back to a micro-task in environments without rAF (Node tests).
   */
  private armCoalesceFlush(): void {
    if (this.flushRafHandle !== -1) return;
    const raf =
      typeof globalThis.requestAnimationFrame === 'function'
        ? globalThis.requestAnimationFrame.bind(globalThis)
        : null;
    if (raf !== null) {
      // Stage 2 first: arm the timeout fallback BEFORE rAF so `flushCoalesceQueue`
      // can `clearTimeout` it even if the rAF callback fires *synchronously*
      // during `raf(...)` (a test scenario; real browsers schedule it async).
      // Whichever stage fires first runs `flushCoalesceQueue`, which
      // idempotently cancels BOTH stages.
      this.flushTimeoutHandle = setTimeout(() => {
        this.flushCoalesceQueue();
      }, COALESCE_FLUSH_FALLBACK_MS);
      // Stage 1: rAF (wins in the foreground, ~16 ms). Guard against a stub
      // that fires its callback *synchronously* during `raf(...)`: in that
      // case `firedSync` flips true and we must NOT overwrite the now-idle
      // `flushRafHandle` (-1) with the stale handle value.
      let firedSync = false;
      const handle = raf(() => {
        firedSync = true;
        this.flushCoalesceQueue();
      });
      if (!firedSync) {
        this.flushRafHandle = handle;
      }
    } else {
      // No rAF (e.g. Node test env). Flush on next microtask.
      this.flushRafHandle = 0;
      queueMicrotask(() => {
        this.flushRafHandle = -1;
        this.flushTimeoutOnly();
      });
    }
  }

  /**
   * Flush the coalesce queue and tear down BOTH flush-tick stages (rAF +
   * timeout). Idempotent and order-independent so whichever of the two stages
   * fires first leaves the other cancelled and the engine idle. If the queue
   * is still non-empty after the flush (it never should be — `flush` drains
   * fully — but defensively), nothing re-arms here; the next `sendRemote` does.
   */
  private flushCoalesceQueue(): void {
    this.cancelFlushRaf();
    this.clearFlushTimeout();
    this.remoteCoalesce.flush();
  }

  /** Microtask-path flush (no rAF env): just drain, no rAF handle to cancel. */
  private flushTimeoutOnly(): void {
    this.clearFlushTimeout();
    this.remoteCoalesce.flush();
  }

  /** Cancel the armed flush rAF (if any) and mark idle. */
  private cancelFlushRaf(): void {
    if (this.flushRafHandle !== -1 && this.flushRafHandle !== 0) {
      const cancel = globalThis.cancelAnimationFrame;
      if (typeof cancel === 'function') cancel(this.flushRafHandle);
    }
    this.flushRafHandle = -1;
  }

  /** Cancel the armed flush timeout (if any) and mark idle. */
  private clearFlushTimeout(): void {
    if (this.flushTimeoutHandle !== null) {
      clearTimeout(this.flushTimeoutHandle);
      this.flushTimeoutHandle = null;
    }
  }

  // ─── conflict-aware Undo internals (C1-C4) ───────────────────────────────

  private pushCancellable(op: CancellableOp): void {
    if (this.cancellableStack.some((o) => isSameOp(o, op))) return;
    this.cancellableStack.push(op);
    this.notifyCancellableChange();
  }

  private removeCancellable(op: CancellableOp): void {
    const idx = this.cancellableStack.findIndex((o) => isSameOp(o, op));
    if (idx === -1) return;
    this.cancellableStack.splice(idx, 1);
    this.notifyCancellableChange();
  }

  private notifyCancellableChange(): void {
    const snapshot = this.getCancellableOps();
    for (const cb of this.cancellableListeners) {
      try {
        cb(snapshot);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[CanvasDrawingEngine] cancellable-stack listener threw', err);
      }
    }
  }

  /**
   * Coalescing (f4 「同 tick に溜まった cancel を union bbox で 1 パス処理」):
   * defer the actual rewind-patch application to the next microtask so
   * several `stroke.cancelled` events processed synchronously in the same
   * task (e.g. a burst of queued WS messages) collapse into ONE
   * `UndoBaselineStore.drainRewind()` call (which itself unions same-layer
   * bboxes). A microtask is enough to coalesce anything delivered within one
   * synchronous JS turn without adding perceptible latency (rewinds are rare
   * events, not a continuous stream like stroke points).
   */
  private scheduleUndoRewindFlush(): void {
    if (this.undoRewindFlushScheduled) return;
    this.undoRewindFlushScheduled = true;
    queueMicrotask(() => {
      this.undoRewindFlushScheduled = false;
      this.applyPendingUndoRewinds();
    });
  }

  /**
   * Drain `undoStore.drainRewind()` and apply every patch through the SAME
   * `layer.region.apply` pipeline every other engine mutation uses (worker
   * postMessage / inline direct call) — "巻き戻し replay は通常描画と同一
   * パイプライン" (no dedicated fast path, §19.11-family invariant). Each
   * patch's `rgba` is sent as a transferable — it's a fresh, single-use
   * buffer.
   */
  private applyPendingUndoRewinds(): void {
    if (!this.undoStore.hasPendingRewind()) return;
    for (const patch of this.undoStore.drainRewind()) this.sendRegionPatch(patch);
  }

  private sendRegionPatch(patch: {
    layerId: number;
    bbox: { x0: number; y0: number; x1: number; y1: number };
    rgba: Uint8ClampedArray;
  }): void {
    const w = patch.bbox.x1 - patch.bbox.x0 + 1;
    const h = patch.bbox.y1 - patch.bbox.y0 + 1;
    this.send(
      {
        t: 'layer.region.apply',
        layerId: patch.layerId,
        x0: patch.bbox.x0,
        y0: patch.bbox.y0,
        w,
        h,
        rgba: patch.rgba,
      },
      [patch.rgba.buffer],
    );
  }

  /**
   * Time-sliced bake driver (f4 "同期一括 bake しない"): repeatedly calls
   * `undoStore.bakeStep()` on idle ticks until it reports no more work,
   * instead of one synchronous mega-bake per `snapshot.published` (which
   * could stall a frame every ~5 min once a room accumulates hundreds of
   * strokes). Uses `requestIdleCallback` where available (spreads bake work
   * into genuinely idle gaps); falls back to `setTimeout(0)`.
   */
  private scheduleBakeStep(): void {
    if (this.bakeScheduled) return;
    this.bakeScheduled = true;
    const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => number })
      .requestIdleCallback;
    const runNext = typeof idle === 'function' ? () => idle(step) : () => setTimeout(step, 0);
    const step = (): void => {
      if (this.disposed) {
        this.bakeScheduled = false;
        return;
      }
      const more = this.undoStore.bakeStep();
      if (more) {
        runNext();
      } else {
        this.bakeScheduled = false;
      }
    };
    runNext();
  }

  /**
   * Test / debug — the undo store's actually-baked watermark (§f4). Lets
   * tests assert the C1 bake stall / settle-re-pump behaviour (再レビュー
   * 指摘 2, 2026-07-24) through the engine's public surface without
   * reaching into `undoStore` internals.
   */
  getUndoBakeWatermark(): number {
    return this.undoStore.getWatermark();
  }

  /**
   * Test / debug — points removed by remote-queue decimation over the engine's
   * lifetime. Non-zero only when the absolute point cap was exceeded (memory
   * safety net; realistic load never reaches it). Replaces the old
   * `getDroppedSegmentCount` — the queue no longer drops, it decimates.
   */
  getDecimatedPointCount(): number {
    return this.remoteCoalesce.getDecimatedPointCount();
  }

  /**
   * Test / debug — number of postMessages emitted by coalesce-queue flushes
   * over the engine's lifetime. Lower than the raw inbound `stroke.points`
   * count whenever coalescing merged adjacent same-sid runs.
   */
  getCoalescedMsgCount(): number {
    return this.remoteCoalesce.getCoalescedMsgCount();
  }

  /**
   * Resolve every pending `pixel.request` promise to `null`. Called on
   * worker crash and on dispose so caller `await` chains don't hang. The
   * `null` shape matches OOB / unknown-layer (= spuit treats it as a
   * no-op, safe degrade).
   */
  private drainPendingPixelRequests(): void {
    if (this.regionReadRequests.size > 0) {
      const pendingReads = Array.from(this.regionReadRequests.values());
      this.regionReadRequests.clear();
      for (const resolve of pendingReads) {
        try {
          resolve(null);
        } catch {
          // Listener errors must not bring the engine down.
        }
      }
    }
    if (this.pixelRequests.size === 0) return;
    const pending = Array.from(this.pixelRequests.values());
    this.pixelRequests.clear();
    for (const resolve of pending) {
      try {
        resolve(null);
      } catch {
        // Listener errors must not bring the engine down.
      }
    }
  }

  /**
   * W3 Classic レイヤー追加 (2026-05-26) — same as `drainPendingPixelRequests`
   * for the `compositeAll.request` round-trips. Resolves every pending
   * `exportAllLayersBlob` promise to `null` on worker crash / dispose so
   * the capture handler falls back to the display canvas instead of hanging.
   */
  private drainPendingCompositeAllRequests(): void {
    if (this.compositeAllRequests.size === 0) return;
    const pending = Array.from(this.compositeAllRequests.values());
    this.compositeAllRequests.clear();
    for (const resolve of pending) {
      try {
        resolve(null);
      } catch {
        // Listener errors must not bring the engine down.
      }
    }
  }

  /**
   * Same as `drainPendingPixelRequests` for the perf-bridge round-trips.
   * Ack resolvers fire with `undefined`, collect resolvers with `[]` —
   * harness `await` chains complete cleanly on worker death / dispose.
   */
  private drainPendingPerfRequests(): void {
    if (this.perfAckRequests.size > 0) {
      const pending = Array.from(this.perfAckRequests.values());
      this.perfAckRequests.clear();
      for (const resolve of pending) {
        try {
          resolve();
        } catch {
          // Listener errors must not bring the engine down.
        }
      }
    }
    if (this.perfCollectRequests.size > 0) {
      const pending = Array.from(this.perfCollectRequests.values());
      this.perfCollectRequests.clear();
      for (const resolve of pending) {
        try {
          resolve([]);
        } catch {
          // Listener errors must not bring the engine down.
        }
      }
    }
    if (this.latencyCollectRequests.size > 0) {
      const pending = Array.from(this.latencyCollectRequests.values());
      this.latencyCollectRequests.clear();
      for (const resolve of pending) resolve([]);
    }
    if (this.thumbStatsRequests.size > 0) {
      const pending = Array.from(this.thumbStatsRequests.values());
      this.thumbStatsRequests.clear();
      for (const resolve of pending) resolve([]);
    }
  }

  private bootInline(
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
    background?: { rgb: number; alpha: number },
  ): void {
    const ctx = canvas.getContext('2d');
    const core = new EngineCore({ width, height, background, layerCount: CLASSIC_LAYER_COUNT });
    if (ctx !== null) {
      // StrictMode dev double-mount / HMR remount safety: when this canvas
      // has been previously attached at the same dimensions, read back its
      // pixel content into the engine's layer state BEFORE attachBackend
      // calls repaint(). Without this, attachBackend's auto-repaint writes
      // the freshly-constructed (background-only) layer to the canvas,
      // wiping any drawing the previously-disposed engine had laid down.
      // First-time mount stays untouched so initial background paint still
      // lands.
      if (ATTACHED_CANVASES.has(canvas) && canvas.width === width && canvas.height === height) {
        const existing = ctx.getImageData(0, 0, width, height);
        core.applySnapshot([{ id: 0, rgba: new Uint8ClampedArray(existing.data) }]);
      }
      ATTACHED_CANVASES.add(canvas);
      core.attachBackend(makeMainThreadBackend(canvas, ctx));
    }
    this.inlineCore = core;
  }

  private bootWorker(
    factory: () => Worker,
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
    background: { rgb: number; alpha: number } | undefined,
  ): void {
    const worker = factory();
    this.worker = worker;
    worker.onmessage = (ev: MessageEvent<EngineWorkerOutbound>) => {
      const msg = ev.data;
      if (msg.t === 'ready') {
        this.workerReady = true;
        for (const m of this.pendingMessages) {
          if (m.transfer !== undefined && m.transfer.length > 0) {
            worker.postMessage(m.msg, m.transfer);
          } else {
            worker.postMessage(m.msg);
          }
        }
        this.pendingMessages = [];
        return;
      }
      if (msg.t === 'pixel.result') {
        const resolve = this.pixelRequests.get(msg.requestId);
        if (resolve !== undefined) {
          this.pixelRequests.delete(msg.requestId);
          resolve(msg.pixel);
        }
        return;
      }
      if (msg.t === 'region.read.result') {
        const resolve = this.regionReadRequests.get(msg.requestId);
        if (resolve !== undefined) {
          this.regionReadRequests.delete(msg.requestId);
          resolve(msg.region);
        }
        return;
      }
      if (msg.t === 'compositeAll.result') {
        const resolve = this.compositeAllRequests.get(msg.requestId);
        if (resolve !== undefined) {
          this.compositeAllRequests.delete(msg.requestId);
          resolve(
            msg.rgba === null ? null : { rgba: msg.rgba, width: msg.width, height: msg.height },
          );
        }
        return;
      }
      if (msg.t === 'perf.ack') {
        const resolve = this.perfAckRequests.get(msg.requestId);
        if (resolve !== undefined) {
          this.perfAckRequests.delete(msg.requestId);
          resolve();
        }
        return;
      }
      if (msg.t === 'thumb') {
        this.emitLayerThumbnail({
          layerId: msg.layerId,
          width: msg.width,
          height: msg.height,
          rgba: msg.rgba,
        });
        return;
      }
      if (msg.t === 'thumb.state') {
        this.setThumbState('stopped');
        return;
      }
      if (msg.t === 'thumb.stats') {
        const resolve = this.thumbStatsRequests.get(msg.requestId);
        if (resolve !== undefined) {
          this.thumbStatsRequests.delete(msg.requestId);
          resolve(msg.durations);
        }
        return;
      }
      if (msg.t === 'latency.entries') {
        const resolve = this.latencyCollectRequests.get(msg.requestId);
        if (resolve !== undefined) {
          this.latencyCollectRequests.delete(msg.requestId);
          resolve(msg.entries);
        }
        return;
      }
      if (msg.t === 'perf.measures') {
        const resolve = this.perfCollectRequests.get(msg.requestId);
        if (resolve !== undefined) {
          this.perfCollectRequests.delete(msg.requestId);
          resolve(msg.entries);
        }
        return;
      }
      if (msg.t === 'error') {
        // eslint-disable-next-line no-console
        console.error('[CanvasDrawingEngine] worker error', msg.message);
      }
    };
    // PR 2-β (2026-05-17) — crash recovery (B-3 notify-only).
    //
    // When the worker throws an unhandled error / dies, we have lost the
    // OffscreenCanvas pixel state irrecoverably (transferControlToOffscreen
    // is one-shot, the host <canvas> can no longer be drawn to). The
    // pragmatic recovery path is "reload the room" — the WS is still
    // healthy, the server can replay events from snapshot + delta.
    //
    // For α this is acceptable: a worker crash is rare (we've never
    // observed one in PR 1.7 harness × 12 candidates × 3 runs), the user
    // sees one error toast / Sentry-captured frame and reloads. β/γ can
    // upgrade to a structured "force canvas remount via React key bump"
    // path if Sentry data shows non-trivial recurrence.
    //
    // We notify in three ways:
    //   1. console.error for dev visibility.
    //   2. Reject every pending `pixel.request` promise so callers don't
    //      hang waiting for a result that will never arrive.
    //   3. Drain `pendingMessages` so the queue doesn't leak.
    worker.onerror = (ev: ErrorEvent) => {
      // eslint-disable-next-line no-console
      console.error('[CanvasDrawingEngine] worker onerror', ev.message ?? ev);
      this.drainPendingPixelRequests();
      this.drainPendingCompositeAllRequests();
      this.drainPendingPerfRequests();
    };
    worker.onmessageerror = (ev: MessageEvent) => {
      // eslint-disable-next-line no-console
      console.error('[CanvasDrawingEngine] worker onmessageerror', ev);
      this.drainPendingPixelRequests();
      this.drainPendingCompositeAllRequests();
      this.drainPendingPerfRequests();
    };
    // `transferControlToOffscreen` is a one-shot — once called, the
    // OffscreenCanvas is the only way to draw on the element.
    type CanvasWithTransfer = HTMLCanvasElement & {
      transferControlToOffscreen?: () => OffscreenCanvas;
    };
    const cwt = canvas as CanvasWithTransfer;
    if (cwt.transferControlToOffscreen === undefined) {
      throw new Error('transferControlToOffscreen unavailable');
    }
    const off = cwt.transferControlToOffscreen.call(canvas);
    const init: EngineWorkerInbound = {
      t: 'init',
      canvas: off,
      width,
      height,
      layerCount: CLASSIC_LAYER_COUNT,
      ...(background !== undefined ? { background } : {}),
    };
    worker.postMessage(init, [off]);
  }

  private startIdleWatchdog(): void {
    // Run every quarter of the timeout so worst-case overhang is ~25%.
    const interval = Math.max(1_000, Math.floor(this.idleStrokeTimeoutMs / 4));
    const tick = (): void => {
      if (this.disposed) return;
      const cutoffMs = Date.now() - this.idleStrokeTimeoutMs;
      this.send({ t: 'force-end-idle', cutoffMs });
    };
    this.idleTimer = setInterval(tick, interval);
  }

  /**
   * Phase 6.9 TTL (2026-05-06): start the per-instance NameLabel expire pass.
   * Cadence is fixed at `EXPIRE_TICK_MS` (100 ms) — fast enough that the
   * worst-case eviction lag (≈100 ms past the 1-second TTL) is invisible to
   * users. The body short-circuits when `nameLabels` is empty so most ticks
   * are a single Map.size === 0 check.
   *
   * Kept separate from `idleTimer` (= 30s-ish stuck-stroke watchdog) because
   * the cadences differ by ~2 orders of magnitude; sharing would force one
   * or the other into a wrong duty cycle.
   */
  private startNameLabelExpireTimer(): void {
    const tick = (): void => {
      if (this.disposed) return;
      this.expireOldNameLabels();
    };
    this.expireTimer = setInterval(tick, EXPIRE_TICK_MS);
  }

  /**
   * Drop labels whose `birthtime` is older than `NAME_LABEL_TTL_MS` and fire
   * the listener once if anything was evicted. `pendingStrokes` is left
   * intact: a still-active in-flight stroke keeps refreshing the per-uid
   * label on each segment, so eviction is purely a "stale display state"
   * cleanup — once the next segment arrives, a fresh entry will reappear
   * with the new `birthtime`.
   *
   * Visible to internal callers via `setInterval`; not part of the public
   * API. Tests exercise it indirectly via `vi.advanceTimersByTime` so the
   * interval fires deterministically.
   */
  private expireOldNameLabels(): void {
    if (this.nameLabels.size === 0) return;
    const cutoff = Date.now() - NAME_LABEL_TTL_MS;
    let changed = false;
    for (const [uid, label] of this.nameLabels) {
      if (label.birthtime < cutoff) {
        this.nameLabels.delete(uid);
        changed = true;
      }
    }
    if (changed) this.notifyNameLabelListeners();
  }

  private guardLive(): void {
    if (this.disposed) {
      throw new Error('CanvasDrawingEngine: method called after dispose()');
    }
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────

/**
 * Shared empty filter-set sentinel — every engine starts with the same
 * frozen instance to avoid pointless allocations + identity churn for the
 * common case of "no filter set yet".
 */
const EMPTY_FILTER_SET: ReadonlySet<string> = new Set<string>();

/** Shared empty result for `addPoints` / `endStroke` early returns (no alloc). */
const EMPTY_POINTS: readonly StrokePoint[] = Object.freeze([]);

/**
 * Tracks `<canvas>` DOM elements that have ever had an inline engine
 * attached. Used by `bootInline` to distinguish "first mount" from
 * "re-mount on the same canvas" so the second-and-onward mount preserves
 * the existing pixel content (StrictMode double-mount / HMR remount /
 * future canvas remount paths).
 *
 * `WeakSet` so DOM elements can be GC'd normally — we don't pin canvases
 * alive past their React lifecycle.
 */
const ATTACHED_CANVASES = new WeakSet<HTMLCanvasElement>();

/**
 * Phase 6.9 TTL (2026-05-06): cadence of the NameLabel expire pass. 100 ms
 * keeps worst-case eviction lag well below UX threshold (TTL 1000 ms ± 100
 * ms) while costing negligible CPU (per-tick body short-circuits when
 * `nameLabels.size === 0`, which is the common case).
 */
const EXPIRE_TICK_MS = 100;

/** 貼り付けの作り直しを、echo 前の自分の線のために保留する上限 (ms)。 */
const IMAGEPASTE_REPAIR_HOLD_MS = 30_000;

/** 自分の移動の「確定 → echo 適用」のあいだ、echo が戻らなくても保持を自動で外すまでの上限。 */
const OWN_STROKE_HOLD_MAX_MS = 20_000;

/** `readLayerRegion` の戻り: 切り捨て後の矩形と、その生 RGBA (straight-alpha、新しいバッファ)。 */
export interface RegionReadResult {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly rgba: Uint8ClampedArray;
}
/** `ownLiveStrokes` の件数上限。 */
const OWN_LIVE_STROKE_CAP = 64;

/**
 * Coalesce-queue flush fallback delay (2026-05-23). The remote coalesce queue
 * is normally flushed by `requestAnimationFrame` (1 flush/frame), but browsers
 * PAUSE rAF in backgrounded tabs. Without a fallback the queue would never
 * flush in the background and a backgrounded viewer would stop seeing peers'
 * art — worse than before, since we no longer drop (we'd just buffer forever
 * up to the cap).
 *
 * So we ALSO arm a `setTimeout(COALESCE_FLUSH_FALLBACK_MS)`; whichever of rAF /
 * timeout fires first flushes the queue and cancels the other.
 *
 * Value rationale: must be comfortably larger than a normal frame (~16 ms) so
 * the foreground path is ALWAYS won by rAF first (the timeout never fires in
 * the foreground → frame-aligned 1 flush/frame behaviour). In a backgrounded
 * tab browsers clamp timers to ~1 s, so this effectively becomes a ~1 s
 * cadence → the accumulated, coalesced points flush in batches and the curve
 * is fully reconstructed (no loss).
 */
const COALESCE_FLUSH_FALLBACK_MS = 250;

// Phase 6.9 の整数 clampZoom (Math.trunc) は M1-a 分数ズームで `./zoom.ts` の
// 連続値 clamp (`[ZOOM_FIT_MIN, ZOOM_MAX]`) に置き換えた — 定義はそちら参照。

/** Apply an inbound worker-protocol message directly to a local EngineCore. */
const applyMessageInline = (core: EngineCore, msg: EngineWorkerInbound): void => {
  switch (msg.t) {
    case 'init':
      // Inline path doesn't init via message — bootInline already did.
      return;
    case 'resize':
      core.resize(msg.width, msg.height);
      return;
    case 'stroke.start':
      core.startStroke({
        sid: msg.sid,
        pen: msg.pen,
        color: msg.color,
        alpha: msg.alpha,
        width: msg.width,
        layerId: msg.layerId,
        mask: msg.mask,
        aa: msg.aa,
        // texture preset — keep the inline path in lockstep with the worker
        // handler (drawingWorker.ts) so forceInline browsers / tests render
        // texture too (2026-06-19 bug: this inline handler dropped penParams).
        ...(msg.penParams !== undefined ? { penParams: msg.penParams } : {}),
        nowMs: msg.nowMs,
        ...(msg.own === true ? { own: true } : {}),
      });
      return;
    case 'stroke.points':
      core.addPoints(msg.sid, msg.points, msg.nowMs);
      return;
    case 'stroke.end':
      core.endStroke(msg.sid);
      return;
    case 'clear':
      core.clearLayer(msg.layerId);
      return;
    case 'layer.visible':
      // W3 Classic レイヤー追加 — client-local display visibility toggle.
      core.setLayerVisible(msg.layerId, msg.visible);
      return;
    case 'layer.opacity':
      core.setLayerOpacity(msg.layerId, msg.opacity);
      return;
    case 'layer.opacity.reset':
      core.resetLayerOpacities();
      return;
    case 'layer.opacity.preview.begin':
      core.beginOpacityPreview(msg.layerId);
      return;
    case 'layer.solo.begin':
      core.beginLayerSolo(msg.layerId);
      return;
    case 'layer.solo.end':
      core.endLayerSolo();
      return;
    case 'layer.localMask':
      core.setLocalRegionMask(msg.mask);
      return;
    case 'layer.opacity.preview.update':
      core.updateOpacityPreview(msg.opacity);
      return;
    case 'layer.opacity.preview.end':
      core.endOpacityPreview(msg.commit);
      return;
    case 'lead.set':
      // 表示専用リード線 (modern-layout.md §9.8.1 ①) — inline path mirrors the
      // worker dispatcher.
      core.setLead({
        pen: msg.pen,
        layerId: msg.layerId,
        ...(msg.mask !== undefined ? { mask: msg.mask } : {}),
        points: msg.points,
      });
      return;
    case 'lead.clear':
      core.clearLead();
      return;
    case 'force-end-idle':
      core.forceEndIdle(msg.cutoffMs);
      return;
    case 'snapshot.apply':
      core.applySnapshot(msg.layers, msg.resetAll ?? false);
      return;
    case 'background.set':
      core.setBackground(msg.background);
      return;
    case 'layer.region.apply':
      // conflict-aware Undo B' bbox-local rewind — inline path mirrors the
      // worker dispatcher (workerProtocol.ts).
      core.applyLayerRegion(msg.layerId, msg.x0, msg.y0, msg.w, msg.h, msg.rgba);
      return;
    case 'region.apply':
      applyRegionMove(core, msg.spec, msg.images);
      return;
    case 'region.read.request':
      // 読み取りは inline では `readLayerRegion` が直接 core を読むので、メッセージ経由では来ない。
      return;
    case 'imagepaste.preview.start':
      // Drag perf hot-fix (2026-05-31): inline path mirrors the worker
      // dispatcher — `startImagepasteOverlay` retains rgba, subsequent
      // `move`s only update (x, y).
      core.startImagepasteOverlay(msg.overlay);
      return;
    case 'imagepaste.preview.move':
      core.moveImagepasteOverlay(msg.x, msg.y);
      return;
    case 'imagepaste.preview.setLayer':
      core.setImagepastePreviewLayer(msg.layerId);
      return;
    case 'imagepaste.preview.end':
      core.endImagepasteOverlay();
      return;
    case 'imagepaste.commit':
      core.applyImagepaste({
        layerId: msg.layerId,
        rgba: msg.rgba,
        width: msg.width,
        height: msg.height,
        x: msg.x,
        y: msg.y,
      });
      return;
    case 'thumb.visible':
      core.setThumbnailsVisible(msg.visible);
      return;
    case 'thumb.suspend':
      core.setThumbnailsSuspended(msg.suspended);
      return;
    case 'thumb.jank':
      core.reportThumbnailMainJank(msg.ratio);
      return;
    case 'thumb.tier':
      core.getThumbnailScheduler()?.setTierOverride(msg.tier);
      return;
    case 'thumb.stats.set':
      core.getThumbnailScheduler()?.setRecordStats(msg.enabled);
      return;
    case 'filter.set':
      // Phase 7 Task 7-10a — main thread enforces the filter gate before
      // dispatch (`applyRemoteStroke` short-circuits on `filterUids`),
      // so nothing to do in the inline path. The message is propagated to
      // keep the worker / inline parity (mirror state for forward-compat).
      return;
    case 'dispose':
      core.detachBackend();
      return;
  }
};

/** DisplayBackend over an HTMLCanvasElement + 2D context. */
const makeMainThreadBackend = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
): DisplayBackend => {
  const preview = createOpacityPreviewSurface(ctx, (w, h) => {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  });
  return {
    width: canvas.width,
    height: canvas.height,
    put(rgba, dx, dy, w, h) {
      const img = new ImageData(rgba, w, h);
      ctx.putImageData(img, dx, dy);
    },
    resize(w, h) {
      if (canvas.width === w && canvas.height === h) return false;
      canvas.width = w;
      canvas.height = h;
      return true;
    },
    previewBegin: (sheets) => preview.begin(sheets),
    previewDraw: (v) => preview.draw(v),
    previewEnd: () => preview.end(),
  };
};

const canSupportOffscreenWorker = (canvas: HTMLCanvasElement): boolean => {
  if (typeof Worker === 'undefined') return false;
  if (typeof OffscreenCanvas === 'undefined') return false;
  type CanvasWithTransfer = HTMLCanvasElement & {
    transferControlToOffscreen?: () => OffscreenCanvas;
  };
  const cwt = canvas as CanvasWithTransfer;
  return typeof cwt.transferControlToOffscreen === 'function';
};

// ─── Phase 7 Task 7-8a — internal preview state + default deps ─────────────

interface ImagepastePreviewInternal {
  /** Original bitmap, kept so every resize re-samples from the source (no loss accumulation). */
  readonly source: ImageBitmap;
  readonly rgba: Uint8ClampedArray;
  /** Size `rgba` was actually rendered at (lags `width/height` until the coalesced resample runs). */
  readonly rgbaWidth: number;
  readonly rgbaHeight: number;
  readonly width: number;
  readonly height: number;
  readonly x: number;
  readonly y: number;
  readonly layerId: number;
  readonly locked: boolean;
}

const defaultImagepasteFetch: ImagepasteFetchFn = (url, init) => {
  const f = globalThis.fetch as
    | ((url: string, init?: RequestInit) => Promise<Response>)
    | undefined;
  if (f === undefined) {
    return Promise.reject(new Error('globalThis.fetch is not available'));
  }
  return f.call(globalThis, url, init as RequestInit | undefined);
};

const defaultImagepasteCreateBitmap: ImagepasteCreateBitmapFn = (blob) => {
  const f = globalThis.createImageBitmap as ((b: Blob) => Promise<ImageBitmap>) | undefined;
  if (f === undefined) {
    return Promise.reject(new Error('globalThis.createImageBitmap is not available'));
  }
  return f.call(globalThis, blob);
};

// Mirror SnapshotLoader.defaultDecodeBitmap: render to a work canvas and read
// back. Tests inject a stub.
//
// 2026-08-13 (Sentry WEB-5 と同根): 旧実装は OffscreenCanvas を無条件に要求して
// throw していたため、**iOS 16.3 以前では画像貼り付けが必ず失敗**していた
// (OffscreenCanvas の 2D は Safari 16.4 から)。ここもメインスレッドなので
// `<canvas>` で代替できる → `rasterCanvas` へ委譲。
const defaultImagepasteDecodeBitmap: ImagepasteDecodeBitmapFn = (bitmap) =>
  decodeBitmapToRgba(bitmap);

/**
 * W3 Classic レイヤー追加 (2026-05-26) — default RGBA → PNG `Blob` encoder
 * for `exportAllLayersBlob`. Renders the full-canvas RGBA into a detached
 * `<canvas>` via `putImageData` and reads back `toBlob('image/png')`.
 *
 * 2026-08-13 (capture-download-blob-2026-08-13.md §3): 旧実装は `toDataURL`
 * が同期で済むことを理由に dataURL を返し、それを `<a href>` に直接載せていた
 * が、その経路自体が本番 fb 2 件の原因だった (Chromium 2MB URL 長制限 / iOS
 * Safari が `data:` を DL できない)。`toBlob` の非同期化コストを払って Blob
 * URL 経路に寄せる — 呼び出し側は元々 worker 往復のため既に async。
 *
 * Returns `null` when the DOM canvas / 2D context isn't available, or when
 * `toBlob` yields `null` (the engine caller then falls back to the
 * visibility-aware display canvas). Tests in the node env inject a stub via
 * `pngEncodeFn` since there's no DOM canvas.
 */
const defaultPngEncode: PngEncodeFn = async (rgba, width, height) => {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;
  // The `ImageData` ctor wants a `Uint8ClampedArray<ArrayBuffer>`. The buffer
  // is always plain-ArrayBuffer-backed at runtime (inline = `PixelBuffer`,
  // worker = a transferred `ArrayBuffer`), so the cast is sound — it only
  // narrows the lib's `ArrayBufferLike` (which admits `SharedArrayBuffer`).
  const data = rgba as Uint8ClampedArray<ArrayBuffer>;
  ctx.putImageData(new ImageData(data, width, height), 0, 0);
  return await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
};

/**
 * Mint a UUID v4 for a stroke id. Browsers / Node 22+ ship
 * `crypto.randomUUID`. Engine-owned so the React layer never deals in
 * uuids — see Phase 3 前半 stub design notes.
 */
const generateSid = (): string => {
  if (typeof globalThis.crypto !== 'undefined' && 'randomUUID' in globalThis.crypto) {
    return globalThis.crypto.randomUUID();
  }
  // Non-crypto fallback. Should never run in MVP browsers.
  const bytes = new Array(16).fill(0).map(() => Math.floor(Math.random() * 256));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// ─── PR 2-β follow-up (2026-05-21) — dev-only engine registry ────────────
//
// The e2e perf harness needs to reach the engine instance from inside
// `page.evaluate` so it can route `enableDrawingPerfMeasure()` into the
// worker (= where the hot path lives after PR 2-β). The harness only
// runs against dev builds, so registration is gated by `import.meta.env.DEV`
// at every callsite — production bundles DCE the helpers away.
//
// Implementation note: each Canvas mount creates exactly one engine in the
// React tree, but the harness can open multiple Tabs (= separate JS realms,
// each with its own `window.__OEKAKI_ENGINES__`). Per-Tab state is naturally
// isolated. We expose a `Set` (not "last engine") so a teardown-during-test
// edge case can't leave the set in a state where the harness can't find
// the live engine.

interface DevEngineRegistry {
  readonly engines: Set<CanvasDrawingEngine>;
}

const DEV_REGISTRY_KEY = '__OEKAKI_ENGINES__';

const getDevRegistry = (): DevEngineRegistry => {
  const w = globalThis as unknown as { [DEV_REGISTRY_KEY]?: DevEngineRegistry };
  let reg = w[DEV_REGISTRY_KEY];
  if (reg === undefined) {
    reg = { engines: new Set() };
    w[DEV_REGISTRY_KEY] = reg;
  }
  return reg;
};

const registerEngineForDev = (engine: CanvasDrawingEngine): void => {
  getDevRegistry().engines.add(engine);
};

const unregisterEngineForDev = (engine: CanvasDrawingEngine): void => {
  // Don't lazily create the registry on dispose if no register fired
  // (= prod build path can't reach this anyway, but be defensive).
  const w = globalThis as unknown as { [DEV_REGISTRY_KEY]?: DevEngineRegistry };
  w[DEV_REGISTRY_KEY]?.engines.delete(engine);
};
